<template>
  <div
    v-loading="kpisLoading"
    element-loading-text="Chargement des données..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.8)"
    element-loading-custom-class="dashboard-loading-overlay"
    class="kpis-placeholder"
  >
    <link href="https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.css" rel="stylesheet">

    <div v-if="!hasDataLoaded" class="kpis-placeholder">
      <p>Chargement des données...</p>
      <p class="filters-info">
        Filtres actifs:
        <span v-if="activeFilters.periode.type">{{ activeFilters.periode.type }}</span>
        <span v-if="activeFilters.niveau.type !== 'total'">
          | {{ activeFilters.niveau.type }}:
          {{ activeFilters.niveau.label || activeFilters.niveau.id }}
        </span>
        <span v-if="activeFilters.cultures.length">
          | Cultures: {{ activeFilters.cultures.length }}
        </span>
        <span v-if="activeFilters.certification !== 'toutes'">
          | {{ activeFilters.certification }}
        </span>
      </p>
    </div>
    <div v-else class="kpis-grid">
      <div class="kpi-item" @click.stop="openKpiModal('Parcelles', totalParcelles)">
        <div class="kpi-icon">📍</div>
        <div class="kpi-content">
          <div class="kpi-label">Parcelles totales</div>
          <div class="kpi-value">{{ formatNumber(totalParcelles) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Parcelles géolocalisées', totalParcellesGeolocalisees)">
        <div class="kpi-icon">🗺️</div>
        <div class="kpi-content">
          <div class="kpi-label">Parcelles géolocalisées</div>
          <div class="kpi-value">{{ formatNumber(totalParcellesGeolocalisees) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Contrôleurs', totalControleurs)">
        <div class="kpi-icon">👨‍💼</div>
        <div class="kpi-content">
          <div class="kpi-label">Contrôleurs</div>
          <div class="kpi-value">{{ formatNumber(totalControleurs) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Producteurs', totalProducteurs)">
        <div class="kpi-icon">👥</div>
        <div class="kpi-content">
          <div class="kpi-label">Producteurs</div>
          <div class="kpi-value">{{ formatNumber(totalProducteurs) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Groupements actifs', totalGroupements)">
        <div class="kpi-icon">🏘️</div>
        <div class="kpi-content">
          <div class="kpi-label">Groupements</div>
          <div class="kpi-value">{{ formatNumber(totalGroupements) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Antennes', totalAntennes)">
        <div class="kpi-icon">📡</div>
        <div class="kpi-content">
          <div class="kpi-label">Antennes</div>
          <div class="kpi-value">{{ formatNumber(totalAntennes) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Audits', totalAudits)">
        <div class="kpi-icon">🔍</div>
        <div class="kpi-content">
          <div class="kpi-label">Audits</div>
          <div class="kpi-value">{{ formatNumber(totalAudits) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Contrôles Internes', totalControlesInternes)">
        <div class="kpi-icon">✅</div>
        <div class="kpi-content">
          <div class="kpi-label">Contrôles Internes</div>
          <div class="kpi-value">{{ formatNumber(totalControlesInternes) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Formations', totalFormations)">
        <div class="kpi-icon">📚</div>
        <div class="kpi-content">
          <div class="kpi-label">Formations</div>
          <div class="kpi-value">{{ formatNumber(totalFormations) }}</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Surface Déclassée', surfaceDeclasseeFiltered)">
        <div class="kpi-icon">⚠️</div>
        <div class="kpi-content">
          <div class="kpi-label">Surface Déclassée</div>
          <div class="kpi-value">{{ formatNumber(surfaceDeclasseeFiltered) }} ha</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Surface Certifiée', surfaceCertifieeFiltered)">
        <div class="kpi-icon">🌱</div>
        <div class="kpi-content">
          <div class="kpi-label">Surface Certifiée</div>
          <div class="kpi-value">{{ formatNumber(surfaceCertifieeFiltered) }} ha</div>
        </div>
      </div>
      <div class="kpi-item" @click.stop="openKpiModal('Quantité Collectée', getQuantiteCollecteeDisplay())">
        <div class="kpi-icon">📦</div>
        <div class="kpi-content">
          <div class="kpi-label">Quantité Collectée</div>
          <div class="kpi-value">{{ formatNumber(getQuantiteCollecteeDisplay()) }} t</div>
        </div>
      </div>
    </div>

    <!-- Modal pour afficher les détails du KPI -->
    <el-dialog
      :key="`kpi-modal-${modalKey}`"
      :title="computedModalTitle"
      :visible.sync="kpiModalVisible"
      width="80%"
      class="kpi-detail-modal"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="true"
      :lock-scroll="true"
      :append-to-body="true"
      @close="closeKpiModal"
      @opened="onModalOpened"
    >
      <div v-if="kpiModalVisible" class="kpi-modal-content">
        <div class="kpi-modal-header">
          <div class="kpi-modal-icon">{{ computedKpiIcon }}</div>
          <div class="kpi-modal-info">
            <h3 class="kpi-modal-title">{{ selectedKpiTitle }}</h3>
            <div class="kpi-modal-value">
              <span class="value-label">Valeur actuelle:</span>
              <span class="value-number">{{ computedKpiValue }}</span>
            </div>
          </div>
        </div>
        <div class="kpi-modal-body">
          <p class="kpi-modal-description">
            Cette valeur représente le nombre total de <strong>{{ computedKpiTitleLower }}</strong>
            selon les filtres actuellement appliqués.
          </p>
          <!-- Ici vous pouvez ajouter plus de contenu selon vos besoins -->
          <!-- Cas des parcelles -->
          <div v-if="selectedKpiTitle === 'Parcelles' || selectedKpiTitle === 'Parcelles géolocalisées'">
            <div class="parcelles-table-container">
              <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
                <el-input
                  v-model="parcelleSearchQuery"
                  placeholder="Rechercher par code ou producteur..."
                  prefix-icon="el-icon-search"
                  clearable
                  style="max-width: 400px;"
                />
                <el-radio-group v-model="parcelleExportFormat" size="small" @change="handleExportParcelles">
                  <el-radio-button label="excel">Excel</el-radio-button>
                  <el-radio-button label="pdf">PDF</el-radio-button>
                </el-radio-group>
                <el-select
                  v-model="parcelleGeolocFilter"
                  placeholder="Filtre géolocalisation"
                  clearable
                  style="width: 220px;"
                >
                  <el-option label="Tous" value="tous" />
                  <el-option label="Parcelles géolocalisées" value="geoloc" />
                  <el-option label="Parcelles non géolocalisées" value="non-geoloc" />
                </el-select>
                <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
                  <el-button
                    size="mini"
                    :disabled="parcellePage <= 1"
                    @click="prevParcellePage"
                  >
                    Précédent
                  </el-button>
                  <span>Page {{ parcellePage }} / {{ parcelleTotalPages }}</span>
                  <el-button
                    size="mini"
                    :disabled="parcellePage >= parcelleTotalPages"
                    @click="nextParcellePage"
                  >
                    Suivant
                  </el-button>
                </div>
              </div>
              <el-table
                v-loading="loadingParcelles"
                :data="filteredParcellesList"
                stripe
                border
                style="width: 100%"
                :default-sort="{ prop: 'code', order: 'ascending' }"
                max-height="400"
              >
                <el-table-column prop="code" label="Code" width="120" sortable>
                  <template slot-scope="scope">
                    {{ scope.row.code || (scope.row.parcelle && scope.row.parcelle.code) || 'N/A' }}
                  </template>
                </el-table-column>
                <el-table-column label="Producteur" min-width="180">
                  <template slot-scope="scope">
                    {{ scope.row.nom_producteur || formatProducteurName(scope.row.producteur || scope.row.producteur_data) }}
                  </template>
                </el-table-column>
                <el-table-column label="Groupement" min-width="150">
                  <template slot-scope="scope">
                    {{ scope.row.groupement || scope.row.groupement_nom || 'N/A' }}
                  </template>
                </el-table-column>
                <el-table-column label="Localisation" min-width="200">
                  <template slot-scope="scope">
                    {{ formatLocalisation(scope.row) }}
                  </template>
                </el-table-column>
                <el-table-column prop="superficie" label="Superficie (ha)" width="130" sortable>
                  <template slot-scope="scope">
                    {{ formatNumber(scope.row.superficie || (scope.row.parcelle && scope.row.parcelle.superficie) || 0) }}
                  </template>
                </el-table-column>
                <el-table-column prop="statut" label="Statut" width="120" sortable>
                  <template slot-scope="scope">
                    <el-tag :type="getStatutTagType(getParcelleStatut(scope.row))" size="small">
                      {{ getParcelleStatut(scope.row) || 'N/A' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Certification" width="120">
                  <template slot-scope="scope">
                    <el-tag :type="getParcelleStatut(scope.row) === 'AB' ? 'success' : 'info'" size="small">
                      {{ getParcelleStatut(scope.row) === 'AB' ? 'Bio' : 'Non-bio' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="Culture" min-width="150">
                  <template slot-scope="scope">
                    {{ formatCultures(scope.row.produits || scope.row.cultures) }}
                  </template>
                </el-table-column>
                <el-table-column label="Actions" width="180" fixed="right" align="center">
                  <template slot-scope="scope">
                    <div style="text-align: center;">
                      <el-button
                        v-if="isParcelleGeolocated(scope.row)"
                        type="text"
                        size="small"
                        icon="el-icon-location"
                        @click="voirCarteParcelle(scope.row)"
                      >
                        Carte
                      </el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="filteredParcellesList.length === 0 && !loadingParcelles" class="empty-state">
                <p v-if="parcelleSearchQuery">Aucune parcelle trouvée avec votre recherche.</p>
                <p v-else>Aucune parcelle trouvée avec les filtres sélectionnés.</p>
              </div>
            </div>
          </div>
          <!-- Cas des contrôleurs -->
          <div v-if="selectedKpiTitle === 'Contrôleurs'">
            <div class="controleurs-table-container">
              <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
                <el-input
                  v-model="controleurSearchQuery"
                  placeholder="Rechercher par nom..."
                  prefix-icon="el-icon-search"
                  clearable
                  style="max-width: 400px;"
                />
                <el-radio-group v-model="controleurExportFormat" size="small" @change="handleExportControleurs">
                  <el-radio-button label="excel">Excel</el-radio-button>
                  <el-radio-button label="pdf">PDF</el-radio-button>
                </el-radio-group>
              </div>
              <el-table
                v-loading="loadingControleurs"
                :data="filteredControleursList"
                stripe
                border
                style="width: 100%"
                :default-sort="{ prop: 'nom', order: 'ascending' }"
                max-height="400"
              >
                <el-table-column label="Nom" min-width="200" sortable>
                  <template slot-scope="scope">
                    {{ formatControleurName(scope.row) }}
                  </template>
                </el-table-column>
                <el-table-column label="Unités de production" min-width="200">
                  <template slot-scope="scope">
                    {{ formatUnitesProduction(scope.row) }}
                  </template>
                </el-table-column>
                <el-table-column label="Groupements" min-width="150" sortable>
                  <template slot-scope="scope">
                    {{ formatNombreGroupements(scope.row) }}
                  </template>
                </el-table-column>
                <el-table-column label="Producteurs" min-width="150" sortable>
                  <template slot-scope="scope">
                    {{ formatNombreProducteurs(scope.row) }}
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="filteredControleursList.length === 0 && !loadingControleurs" class="empty-state">
                <p v-if="controleurSearchQuery">Aucun contrôleur trouvé avec votre recherche.</p>
                <p v-else>Aucun contrôleur trouvé avec les filtres sélectionnés.</p>
              </div>
            </div>
          </div>
          <!-- Cas des producteurs -->
          <div v-if="selectedKpiTitle === 'Producteurs'">
            <div class="producteurs-table-container">
              <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap;">
                <el-input
                  v-model="producteurSearchQuery"
                  placeholder="Rechercher par nom, prénom, numéro ou code..."
                  prefix-icon="el-icon-search"
                  clearable
                  style="max-width: 400px;"
                />
                <el-radio-group v-model="producteurExportFormat" size="small" @change="handleExportProducteurs">
                  <el-radio-button label="excel">Excel</el-radio-button>
                  <el-radio-button label="pdf">PDF</el-radio-button>
                </el-radio-group>
                <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
                  <el-button
                    size="mini"
                    :disabled="producteurPage <= 1"
                    @click="prevProducteurPage"
                  >
                    Précédent
                  </el-button>
                  <span>Page {{ producteurPage }} / {{ producteurTotalPages }}</span>
                  <el-button
                    size="mini"
                    :disabled="producteurPage >= producteurTotalPages"
                    @click="nextProducteurPage"
                  >
                    Suivant
                  </el-button>
                </div>
              </div>
              <el-table
                v-loading="loadingProducteurs"
                :data="filteredProducteursList"
                stripe
                border
                style="width: 100%"
                :default-sort="{ prop: 'nom', order: 'ascending' }"
                max-height="400"
              >
                <el-table-column prop="nom" label="Nom" min-width="200" sortable>
                  <template slot-scope="scope">
                    {{ scope.row.nom || 'N/A' }}
                  </template>
                </el-table-column>
                <el-table-column prop="prenom" label="Prénom" min-width="200" sortable>
                  <template slot-scope="scope">
                    {{ scope.row.prenom || 'N/A' }}
                  </template>
                </el-table-column>
                <el-table-column prop="numero" label="Numéro" min-width="150" sortable>
                  <template slot-scope="scope">
                    {{ formatProducteurNumero(scope.row) }}
                  </template>
                </el-table-column>
                <el-table-column prop="code" label="Code" min-width="150" sortable>
                  <template slot-scope="scope">
                    {{ formatProducteurCode(scope.row) }}
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="filteredProducteursList.length === 0 && !loadingProducteurs" class="empty-state">
                <p v-if="producteurSearchQuery">Aucun producteur trouvé avec votre recherche.</p>
                <p v-else>Aucun producteur trouvé avec les filtres sélectionnés.</p>
              </div>
            </div>
          </div>
          <!-- Cas des groupements -->
          <div v-if="selectedKpiTitle === 'Groupements'">
            <p>
              Les groupements sont les groupements de producteurs.
            </p>
          </div>
          <!-- Cas des audits -->
          <div v-if="selectedKpiTitle === 'Audits'">
            <p>
              Les audits sont les audits effectués sur les parcelles.
            </p>
          </div>
          <!-- Cas des contrôles internes -->
          <div v-if="selectedKpiTitle === 'Contrôles Internes'">
            <p>
              Les contrôles internes sont les contrôles effectués par les contrôleurs.
            </p>
          </div>
          <!-- Cas des formations -->
          <div v-if="selectedKpiTitle === 'Formations'">
            <p>
              Les formations sont les formations effectuées par les contrôleurs.
            </p>
          </div>
          <!-- Cas des surfaces déclassées -->
          <div v-if="selectedKpiTitle === 'Surface Déclassée'">
            <p>
              Les surfaces déclassées sont les surfaces déclassées sur les parcelles.
            </p>
          </div>
          <!-- Cas des surfaces certifiées -->
          <div v-if="selectedKpiTitle === 'Surface Certifiée'">
            <p>
              Les surfaces certifiées sont les surfaces certifiées sur les parcelles.
            </p>
          </div>
          <!-- Cas des quantités collectées -->
          <div v-if="selectedKpiTitle === 'Quantité Collectée'">
            <p>
              Les quantités collectées sont les quantités collectées sur les parcelles.
            </p>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closeKpiModal">Fermer</el-button>
      </span>
    </el-dialog>

    <!-- Modal pour afficher la carte de la parcelle -->
    <el-dialog
      :key="`carte-modal-${carteModalKey}`"
      title="Carte de la parcelle"
      :visible="carteModalVisible"
      width="100%"
      top="0"
      class="carte-parcelle-modal fullscreen-modal"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :modal="true"
      :lock-scroll="true"
      :append-to-body="true"
      @close="closeCarteModal"
    >
      <div v-if="carteModalVisible" class="carte-modal-content">
        <div v-if="loadingCoordonnees" class="loading-coordonnees">
          <p>Chargement des coordonnées...</p>
        </div>
        <div v-else-if="coordonneesParcelle.length > 0" id="carte-parcelle-container" class="carte-fullscreen-container">
          <div id="carteParcelleContainer" class="carte-fullscreen-map" />
          <div v-if="selectedParcelleForCarte" class="carte-info-floating">
            <p><strong style="text-decoration: underline;">Code:</strong> {{ selectedParcelleForCarte.code || (selectedParcelleForCarte.parcelle && selectedParcelleForCarte.parcelle.code) || 'N/A' }}</p>
            <p><strong style="text-decoration: underline;">Producteur:</strong> {{ formatProducteurName(selectedParcelleForCarte.producteur || selectedParcelleForCarte.producteur_data) }}</p>
            <p><strong style="text-decoration: underline;">Groupement:</strong> {{ selectedParcelleForCarte.groupement || selectedParcelleForCarte.groupement_nom || 'N/A' }}</p>
            <p><strong style="text-decoration: underline;">Localisation:</strong> {{ formatLocalisation(selectedParcelleForCarte) }}</p>
            <p><strong style="text-decoration: underline;">Superficie:</strong> {{ formatNumber(selectedParcelleForCarte.superficie || (selectedParcelleForCarte.parcelle && selectedParcelleForCarte.parcelle.superficie) || 0) }} ha</p>
            <p><strong style="text-decoration: underline;">Statut:</strong> {{ getParcelleStatut(selectedParcelleForCarte) || 'N/A' }}</p>
            <p><strong style="text-decoration: underline;">Certification:</strong> {{ getParcelleStatut(selectedParcelleForCarte) === 'AB' ? 'Bio' : 'Non-bio' }}</p>
            <p><strong style="text-decoration: underline;">Culture:</strong> {{ formatCultures(selectedParcelleForCarte.produits || selectedParcelleForCarte.cultures) }}</p>
          </div>
          <button id="zoomto-parcelle" class="btn-control-carte">Voir la parcelle</button>
        </div>
        <div v-else class="no-coordonnees">
          <el-tag type="info">Cette parcelle n'a pas de coordonnées GPS</el-tag>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closeCarteModal">Fermer</el-button>
      </span>
    </el-dialog>

    <el-divider v-if="hasKpiResults" style="margin: 24px 0;" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Resource from '@/api/resource';
import request from '@/utils/request';
import mapboxgl from 'mapbox-gl';
import { export_json_to_excel } from '@/vendor/Export2Excel';
import jsPDF from 'jspdf';

const campagnesResource = new Resource('campagne_agricoles');
const parcelleResource = new Resource('parcelles');
const coordonneeResource = new Resource('coordonnees');
const uniteProductionResource = new Resource('unite_productions');

export default {
  name: 'KpiPanel',
  filters: {
    numberFormat(value) {
      if (!value) {
        return '0';
      }
      return new Intl.NumberFormat('fr-FR').format(value);
    },
  },
  props: {
    parcelles: {
      type: Array,
      default: () => [],
    },
    parcellesProducteurs: {
      type: Array,
      default: () => [],
    },
    parcellesGeolocalisees: {
      type: Array,
      default: () => [],
    },
    controleurs: {
      type: Array,
      default: () => [],
    },
    producteurs: {
      type: Array,
      default: () => [],
    },
    groupements: {
      type: Array,
      default: () => [],
    },
    unitesProductions: {
      type: Array,
      default: () => [],
    },
    audits: {
      type: Array,
      default: () => [],
    },
    controlesInternes: {
      type: Array,
      default: () => [],
    },
    formations: {
      type: Array,
      default: () => [],
    },
    surfaceCertifiee: {
      type: Number,
      default: 0,
    },
    surfaceDeclassee: {
      type: Number,
      default: 0,
    },
    quantiteCollectee: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      kpisLoading: false,
      lastUpdate: null,
      kpis: {},
      campagneId: null,
      campagneInfo: null,
      fixedHeaderOffset: 0,
      layoutBodyOffset: 0,
      localPeriode: {
        type: 'campagne',
        date_debut: null,
        date_fin: null,
      },
      quickFilters: {
        antenneId: null,
        ciId: null,
        groupementId: null,
        producteurId: null,
        cultures: [],
        certification: 'toutes',
      },
      producteurOptions: [],
      loadingProducteurs: false,
      kpiModalVisible: false,
      selectedKpiTitle: '',
      selectedKpiValue: null,
      modalKey: 0,
      // Limiter le nombre d'entrées affichées dans la modal pour éviter de figer le navigateur
      parcelleDisplayLimit: 500,
      parcellePage: 1,
      parcellePageSize: 500,
      parcellesList: [],
      parcellesListForCount: [], // Liste pour le calcul du nombre de parcelles géolocalisées
      parcelleSearchQuery: '',
      parcelleExportFormat: null,
      parcelleGeolocFilter: 'tous',
      loadingParcelles: false,
      controleursList: [],
      controleurSearchQuery: '',
      controleurExportFormat: null,
      loadingControleurs: false,
      controleursProducteursCount: {}, // Cache pour stocker le nombre de producteurs par contrôleur
      unitesProductionsCache: {}, // Cache pour stocker les unités de production récupérées par ID
      producteurPage: 1,
      producteurPageSize: 500,
      producteurDisplayLimit: 500,
      producteursList: [],
      producteurSearchQuery: '',
      producteurExportFormat: null,
      quantiteCollecteeFilteredValue: null,
      loadingQuantiteCollectee: false,
      filteredKpis: {
        totalParcelles: 0,
        totalParcellesGeolocalisees: 0,
        totalProducteurs: 0,
        totalControleurs: 0,
        totalGroupements: 0,
        totalAntennes: 0,
        totalAudits: 0,
        totalControlesInternes: 0,
        totalQuantiteCollectee: 0,
      },
      // Flag pour indiquer que les données initiales sont chargées
      // Une fois chargées, les KPIs principaux ne doivent plus être modifiés par les filtres
      initialDataLoaded: false,
      carteModalVisible: false,
      carteModalKey: 0,
      selectedParcelleForCarte: null,
      coordonneesParcelle: [],
      loadingCoordonnees: false,
      mapInstance: null,
      mapboxAccessToken: 'pk.eyJ1IjoiZGV2b25lc295IiwiYSI6ImNsMTR0c2Y0MDBkdWIzZHF6cjk5OWJ1NnMifQ.cEu2E93WCC2rY74DEjqixg',
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters', 'activeFilters', 'filterOptions']),
    // Données filtrées selon les filtres actifs
    filteredParcelles() {
      return this.getFilteredParcelles();
    },
    filteredControleurs() {
      return this.getFilteredControleurs();
    },
    filteredProducteurs() {
      return this.getFilteredProducteurs();
    },
    filteredGroupements() {
      return this.getFilteredGroupements();
    },
    filteredUnitesProductions() {
      return this.getFilteredUnitesProductions();
    },
    filteredAudits() {
      return this.getFilteredAudits();
    },
    filteredControlesInternes() {
      return this.getFilteredControlesInternes();
    },
    filteredFormations() {
      return this.getFilteredFormations();
    },
    // Totaux basés sur les données filtrées
    totalParcelles() {
      return this.filteredParcelles ? this.filteredParcelles.length : 0;
    },
    totalParcellesGeolocalisees() {
      const filtered = this.filteredParcelles || [];
      return filtered.filter(parcelle => {
        const gps = parcelle.gps;
        return gps && gps !== null && gps !== undefined && gps !== '' && gps.trim() !== '';
      }).length;
    },
    totalControleurs() {
      return this.filteredControleurs ? this.filteredControleurs.length : 0;
    },
    totalProducteurs() {
      return this.filteredProducteurs ? this.filteredProducteurs.length : 0;
    },
    totalGroupements() {
      return this.filteredGroupements ? this.filteredGroupements.length : 0;
    },
    totalAntennes() {
      return this.filteredUnitesProductions ? this.filteredUnitesProductions.length : 0;
    },
    totalAudits() {
      return this.filteredAudits ? this.filteredAudits.length : 0;
    },
    totalControlesInternes() {
      return this.filteredControlesInternes ? this.filteredControlesInternes.length : 0;
    },
    totalFormations() {
      return this.filteredFormations ? this.filteredFormations.length : 0;
    },
    surfaceCertifieeFiltered() {
      // Calculer la surface certifiée à partir des parcelles filtrées
      // Surface certifiée = somme des superficies des parcelles avec statut 'AB'
      const filteredParcelles = this.filteredParcelles || [];
      const total = filteredParcelles.reduce((sum, parcelle) => {
        const statut = this.getParcelleStatut(parcelle);
        if (statut === 'AB') {
          const superficie = parcelle.superficie || (parcelle.parcelle && parcelle.parcelle.superficie) || 0;
          return sum + (Number(superficie) || 0);
        }
        return sum;
      }, 0);
      // Arrondir à 2 décimales comme le backend
      return Math.round(total * 100) / 100;
    },
    surfaceDeclasseeFiltered() {
      // Calculer la surface déclassée à partir des parcelles filtrées
      // Surface déclassée = somme des superficies des parcelles avec statut 'C1', 'C2' ou 'C3'
      const filteredParcelles = this.filteredParcelles || [];
      const total = filteredParcelles.reduce((sum, parcelle) => {
        const statut = this.getParcelleStatut(parcelle);
        if (statut === 'C1' || statut === 'C2' || statut === 'C3') {
          const superficie = parcelle.superficie || (parcelle.parcelle && parcelle.parcelle.superficie) || 0;
          return sum + (Number(superficie) || 0);
        }
        return sum;
      }, 0);
      // Arrondir à 2 décimales comme le backend
      return Math.round(total * 100) / 100;
    },
    quantiteCollecteeFiltered() {
      // Si la valeur filtrée a été calculée (même si c'est 0), l'utiliser
      // null/undefined signifie qu'aucun calcul n'a été fait, donc utiliser la valeur globale
      const currentValue = this.quantiteCollecteeFilteredValue;

      // Vérifier explicitement si la valeur est null ou undefined
      // Utiliser une comparaison stricte pour éviter les problèmes avec 0
      if (currentValue != null) {
        // currentValue n'est ni null ni undefined, donc utiliser cette valeur
        return currentValue;
      }

      // Sinon, utiliser la valeur globale
      return this.quantiteCollectee || 0;
    },
    camp_id() {
      return this.$store.getters['camp_agricole/camp_id'] || sessionStorage.getItem('camp_id');
    },
    campagneDisplayYear() {
      if (this.campagneInfo) {
        return (
          this.campagneInfo.annee ||
            this.campagneInfo.nom ||
            this.campagneInfo.libelle ||
            this.campagneInfo.label ||
            null
        );
      }
      return this.getFallbackCampaignYear();
    },
    periodeDisplay() {
      const periode = this.localPeriode || {};
      const type = periode.type || '';
      if (!type) {
        return 'Non défini';
      }

      if (type === 'hebdo') {
        const dateReference = periode.date_debut || periode.date_fin || this.getTodayISODate();
        const { start, end, weekNumber } = this.getWeekRange(dateReference);
        const weekLabel = weekNumber ? `Semaine ${weekNumber}` : 'Semaine non définie';
        const formattedStart = this.formatDisplayDate(start);
        const formattedEnd = this.formatDisplayDate(end);
        if (formattedStart && formattedEnd) {
          return `${weekLabel} (${formattedStart} - ${formattedEnd})`;
        }
        return `${weekLabel}`;
      }

      if (type === 'journalier') {
        const referenceDate = periode.date_debut || this.getTodayISODate();
        const formattedDate = this.formatDisplayDate(referenceDate);
        if (formattedDate) {
          return `${formattedDate}`;
        }
        return 'Journalier';
      }

      if (type === 'custom') {
        const formattedStart = this.formatDisplayDate(periode.date_debut);
        const formattedEnd = this.formatDisplayDate(periode.date_fin);
        if (formattedStart && formattedEnd) {
          return `Personnalisé - ${formattedStart} - ${formattedEnd}`;
        }
        if (formattedStart || formattedEnd) {
          return `Personnalisé - ${formattedStart || formattedEnd}`;
        }
        return 'Personnalisé';
      }

      if (type === 'campagne') {
        return `${this.campagneDisplayYear || 'N/A'}`;
      }

      return type;
    },
    hasKpiResults() {
      // Les KPIs sont maintenant calculés depuis les props, donc toujours disponibles
      return true;
    },
    hasDataLoaded() {
      // Vérifier si les données sont chargées (au moins une des listes n'est pas vide)
      return this.parcelles && this.parcelles.length >= 0;
    },
    filteredParcellesList() {
      let filtered = this.parcellesList;

      // Si on est dans la modal "Parcelles géolocalisées", filtrer automatiquement
      const shouldFilterGeoloc = this.selectedKpiTitle === 'Parcelles géolocalisées';

      // Filtrer par géolocalisation
      if (shouldFilterGeoloc || (this.parcelleGeolocFilter && this.parcelleGeolocFilter !== 'tous')) {
        filtered = filtered.filter(parcelle => {
          const isGeolocated = this.isParcelleGeolocated(parcelle);
          if (shouldFilterGeoloc || this.parcelleGeolocFilter === 'geoloc') {
            return isGeolocated;
          } else if (this.parcelleGeolocFilter === 'non-geoloc') {
            return !isGeolocated;
          }
          return true;
        });
      }

      // Filtrer par recherche (code, producteur ou culture)
      if (this.parcelleSearchQuery && this.parcelleSearchQuery.trim() !== '') {
        const searchTerm = this.parcelleSearchQuery.toLowerCase().trim();

        filtered = filtered.filter(parcelle => {
          // Recherche par code
          const code = parcelle.code || (parcelle.parcelle && parcelle.parcelle.code) || '';
          if (code.toString().toLowerCase().includes(searchTerm)) {
            return true;
          }

          // Recherche par producteur
          const producteurName = this.formatProducteurName(
            parcelle.producteur || parcelle.producteur_data
          );
          if (producteurName.toLowerCase().includes(searchTerm)) {
            return true;
          }

          // Recherche par culture(s)
          const cultures = parcelle.produits || parcelle.cultures || [];
          // Dans index.vue, on a éventuellement enrichi avec un tableau de noms de cultures
          if (Array.isArray(cultures)) {
            const culturesStr = cultures.join(', ').toLowerCase();
            if (culturesStr.includes(searchTerm)) {
              return true;
            }
          } else if (typeof cultures === 'string') {
            if (cultures.toLowerCase().includes(searchTerm)) {
              return true;
            }
          }

          return false;
        });
      }

      // Pagination côté client
      const size = this.parcellePageSize || this.parcelleDisplayLimit || filtered.length;
      const page = this.parcellePage || 1;
      const start = (page - 1) * size;
      const end = start + size;
      return filtered.slice(start, end);
    },
    parcelleTotalPages() {
      if (!this.parcellesList || this.parcellesList.length === 0) {
        return 1;
      }
      const size = this.parcellePageSize || this.parcelleDisplayLimit || this.parcellesList.length;
      return Math.max(1, Math.ceil(this.parcellesList.length / size));
    },
    filteredProducteursList() {
      let filtered = this.producteursList;

      // Filtrer par recherche (nom, prénom ou numéro)
      if (this.producteurSearchQuery && this.producteurSearchQuery.trim() !== '') {
        const searchTerm = this.producteurSearchQuery.toLowerCase().trim();

        filtered = filtered.filter(producteur => {
          // Recherche par nom
          const nom = (producteur.nom || '').toLowerCase();
          if (nom.includes(searchTerm)) {
            return true;
          }

          // Recherche par prénom
          const prenom = (producteur.prenom || '').toLowerCase();
          if (prenom.includes(searchTerm)) {
            return true;
          }

          // Recherche par numéro
          const numero = (producteur.numero || '').toString().toLowerCase();
          if (numero.includes(searchTerm)) {
            return true;
          }

          // Recherche par code
          const code = (producteur.code || '').toString().toLowerCase();
          if (code.includes(searchTerm)) {
            return true;
          }

          return false;
        });
      }

      // Pagination côté client
      const size = this.producteurPageSize || this.producteurDisplayLimit || filtered.length;
      const page = this.producteurPage || 1;
      const start = (page - 1) * size;
      const end = start + size;
      return filtered.slice(start, end);
    },
    producteurTotalPages() {
      if (!this.producteursList || this.producteursList.length === 0) {
        return 1;
      }
      // Calculer le nombre total de pages basé sur la liste filtrée (avant pagination)
      let filtered = this.producteursList;
      if (this.producteurSearchQuery && this.producteurSearchQuery.trim() !== '') {
        const searchTerm = this.producteurSearchQuery.toLowerCase().trim();
        filtered = filtered.filter(producteur => {
          const nom = (producteur.nom || '').toLowerCase();
          const prenom = (producteur.prenom || '').toLowerCase();
          const numero = (producteur.numero || '').toString().toLowerCase();
          const code = (producteur.code || '').toString().toLowerCase();
          return nom.includes(searchTerm) || prenom.includes(searchTerm) || numero.includes(searchTerm) || code.includes(searchTerm);
        });
      }
      const size = this.producteurPageSize || this.producteurDisplayLimit || filtered.length;
      return Math.max(1, Math.ceil(filtered.length / size));
    },
    filteredControleursList() {
      if (!this.controleursList || this.controleursList.length === 0) {
        return [];
      }

      let filtered = [];

      // Si pas de recherche, retourner toute la liste
      if (!this.controleurSearchQuery || this.controleurSearchQuery.trim() === '') {
        filtered = this.controleursList;
      } else {
        const searchTerm = this.controleurSearchQuery.toLowerCase().trim();
        filtered = this.controleursList.filter(controleur => {
          // Recherche uniquement par nom
          const nom = this.formatControleurName(controleur).toLowerCase();
          return nom.includes(searchTerm);
        });
      }

      // Trier par ordre alphabétique (A à Z) basé sur le nom complet
      return filtered.sort((a, b) => {
        const nomA = this.formatControleurName(a).toLowerCase();
        const nomB = this.formatControleurName(b).toLowerCase();
        return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base' });
      });
    },
    nombreParcellesGeolocalisees() {
      // Utiliser directement la valeur depuis les KPIs du backend si disponible
      if (this.kpis && this.kpis.nombre_parcelles_geo !== undefined) {
        return this.kpis.nombre_parcelles_geo;
      }

      // Si les KPIs ne sont pas encore chargés, retourner 0
      // Note: Le calcul côté client via loadParcellesForCount() a été supprimé pour optimiser les performances
      return 0;
    },
    antenneOptions() {
      const allAntennes = this.getNiveauOptionsByType('antenne');

      // Si un CI est sélectionné, filtrer les antennes liées à ce CI
      if (this.quickFilters.ciId) {
        const antenneIds = this.getValidAntenneIdsForCI(this.quickFilters.ciId);
        return allAntennes.filter(antenne => antenneIds.includes(antenne.id));
      }

      return allAntennes;
    },
    ciOptions() {
      return this.getNiveauOptionsByType('ci');
    },
    groupementOptions() {
      const allGroupements = this.getNiveauOptionsByType('groupement');
      let filtered = allGroupements;

      // Si un CI est sélectionné, filtrer les groupements de ce CI
      if (this.quickFilters.ciId) {
        filtered = filtered.filter(
          grp => grp.controleur_id === this.quickFilters.ciId
        );
      }

      // Si une antenne est sélectionnée, filtrer aussi par antenne
      if (this.quickFilters.antenneId) {
        filtered = filtered.filter(
          grp => grp.unite_production_id === this.quickFilters.antenneId
        );
      }

      return filtered;
    },
    cultureOptions() {
      return this.filterOptions?.cultures || [];
    },
    certificationOptions() {
      const certifs = this.filterOptions?.certifications || {};
      return Object.keys(certifs).map(key => certifs[key]);
    },
    activeFiltersSummary() {
      const parts = [];

      // CI
      if (this.quickFilters.ciId) {
        const ci = this.ciOptions.find(c => c.id === this.quickFilters.ciId);
        if (ci) {
          parts.push(this.formatNiveauLabel(ci));
        }
      }

      // Antenne
      if (this.quickFilters.antenneId) {
        const antenne = this.antenneOptions.find(a => a.id === this.quickFilters.antenneId);
        if (antenne) {
          parts.push(this.formatNiveauLabel(antenne));
        }
      }

      // Groupement
      if (this.quickFilters.groupementId) {
        const groupement = this.groupementOptions.find(g => g.id === this.quickFilters.groupementId);
        if (groupement) {
          parts.push(this.formatNiveauLabel(groupement));
        }
      }

      // Producteur
      if (this.quickFilters.producteurId) {
        const producteur = this.producteurOptions.find(p => p.id === this.quickFilters.producteurId);
        if (producteur) {
          parts.push(this.formatProducteurLabel(producteur));
        }
      }

      // Cultures
      if (this.quickFilters.cultures && this.quickFilters.cultures.length > 0) {
        const cultureNames = this.quickFilters.cultures
          .map(cultureId => {
            const culture = this.cultureOptions.find(c => c.id === cultureId);
            return culture ? culture.nom : null;
          })
          .filter(Boolean);
        if (cultureNames.length > 0) {
          parts.push(`(${cultureNames.join(',')})`);
        }
      }

      // Certification
      if (this.quickFilters.certification && this.quickFilters.certification !== 'toutes') {
        const certif = this.certificationOptions.find(c => c.value === this.quickFilters.certification);
        if (certif) {
          parts.push(certif.label);
        }
      }

      return parts.length > 0 ? parts.join(' -> ') : 'Aucun filtre sélectionné';
    },
    modalTitle() {
      return this.selectedKpiTitle ? `Détails - ${this.selectedKpiTitle}` : 'Détails du KPI';
    },
    computedModalTitle() {
      return this.selectedKpiTitle ? `Détails - ${this.selectedKpiTitle}` : 'Détails du KPI';
    },
    computedKpiIcon() {
      return this.getKpiIcon(this.selectedKpiTitle);
    },
    computedKpiValue() {
      return this.formatNumber(this.selectedKpiValue);
    },
    computedKpiTitleLower() {
      return this.selectedKpiTitle ? this.selectedKpiTitle.toLowerCase() : '';
    },
  },
  watch: {
    'activeFilters.periode': {
      handler(newVal) {
        if (newVal) {
          this.localPeriode = { ...newVal };
        }
      },
      deep: true,
      immediate: true,
    },
    filters: {
      handler(newFilters) {
        this.syncQuickFilters(newFilters);
      },
      deep: true,
      immediate: true,
    },
    camp_id: {
      handler(newCampId) {
        if (newCampId) {
          const campIdNum = parseInt(newCampId, 10);
          this.campagneId = campIdNum;
          // Mettre à jour les infos de la campagne
          this.updateCampagneInfo(campIdNum);
          // Recharger les KPIs avec la nouvelle campagne
          if (this.activeFilters && Object.keys(this.activeFilters).length > 0) {
            // Les filtres sont maintenant appliqués via les computed properties
          }
        }
      },
      immediate: true,
    },
    'quickFilters.ciId': {
      handler(newCiId, oldCiId) {
        // Si un CI est sélectionné, vérifier que l'antenne sélectionnée est valide
        if (newCiId && this.quickFilters.antenneId) {
          const validAntenneIds = this.getValidAntenneIdsForCI(newCiId);
          if (!validAntenneIds.includes(this.quickFilters.antenneId)) {
            // L'antenne sélectionnée n'est plus valide, la réinitialiser
            this.quickFilters.antenneId = null;
          }
        }

        // Si le CI change, réinitialiser aussi les groupements et producteurs
        // car ils dépendent du CI
        if (newCiId !== oldCiId && newCiId) {
          // Vérifier si le groupement sélectionné appartient au nouveau CI
          if (this.quickFilters.groupementId) {
            const validGroupementIds = this.getValidGroupementIdsForCI(newCiId);
            if (!validGroupementIds.includes(this.quickFilters.groupementId)) {
              this.quickFilters.groupementId = null;
              this.quickFilters.producteurId = null;
            }
          }
        }
      },
      immediate: false,
    },
    'quickFilters.antenneId': {
      handler(newAntenneId, oldAntenneId) {
        // Si l'antenne change, vérifier que le groupement sélectionné est valide
        if (newAntenneId !== oldAntenneId && newAntenneId) {
          if (this.quickFilters.groupementId) {
            const validGroupementIds = this.getValidGroupementIds(newAntenneId);
            if (!validGroupementIds.includes(this.quickFilters.groupementId)) {
              this.quickFilters.groupementId = null;
              this.quickFilters.producteurId = null;
            }
          }
        }
      },
      immediate: false,
    },
    'quickFilters.groupementId': {
      async handler(newGroupementId, oldGroupementId) {
        // Quand un groupement est sélectionné, charger automatiquement les producteurs de ce groupement
        if (newGroupementId !== oldGroupementId) {
          if (newGroupementId) {
            // Charger les producteurs du groupement sélectionné
            await this.loadProducteursOptions();
            // Vérifier si le producteur sélectionné appartient au nouveau groupement
            if (this.quickFilters.producteurId) {
              const validProducteurIds = this.producteurOptions.map(p => p.id);
              if (!validProducteurIds.includes(this.quickFilters.producteurId)) {
                this.quickFilters.producteurId = null;
              }
            }
          } else {
            // Si le groupement est désélectionné, réinitialiser les producteurs
            this.quickFilters.producteurId = null;
            this.producteurOptions = [];
          }
        }
      },
      immediate: false,
    },
    kpiModalVisible(newVal) {
      if (newVal) {
        // Bloquer le scroll du body et html quand la modal s'ouvre
        const scrollY = window.scrollY;
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.position = 'fixed';
        document.documentElement.style.width = '100%';
        document.documentElement.style.height = '100%';
        document.documentElement.style.top = `-${scrollY}px`;
        document.documentElement.classList.add('kpi-modal-open');

        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.classList.add('kpi-modal-open');
        document.body.setAttribute('data-scroll-y', scrollY);
      } else {
        // Restaurer le scroll du body et html quand la modal se ferme
        const scrollY = document.body.getAttribute('data-scroll-y') || '0';
        document.documentElement.style.overflow = '';
        document.documentElement.style.position = '';
        document.documentElement.style.width = '';
        document.documentElement.style.height = '';
        document.documentElement.style.top = '';
        document.documentElement.classList.remove('kpi-modal-open');

        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.classList.remove('kpi-modal-open');
        document.body.removeAttribute('data-scroll-y');

        // Restaurer la position de scroll
        window.scrollTo(0, parseInt(scrollY, 10));
      }
    },
    carteModalVisible(newVal) {
      // Quand la modal s'ouvre, initialiser la carte après un court délai
      if (newVal && this.coordonneesParcelle.length > 0) {
        // Attendre que le DOM soit complètement rendu
        setTimeout(() => {
          this.$nextTick(() => {
            this.initMap();
          });
        }, 300);
      } else if (!newVal) {
        // Nettoyer la carte quand la modal se ferme
        if (this.mapInstance) {
          this.mapInstance.remove();
          this.mapInstance = null;
        }
      }
    },
    quantiteCollecteeFilteredValue(newVal, oldVal) {
      console.log('[Quantité Collectée] Watcher quantiteCollecteeFilteredValue - Ancienne valeur:', oldVal, '| Nouvelle valeur:', newVal);
      // Si la valeur passe de quelque chose à null, logger un avertissement
      if (oldVal != null && newVal === null) {
        console.warn('[Quantité Collectée] ⚠️ ATTENTION: La valeur est passée de', oldVal, 'à null !');
        console.trace('[Quantité Collectée] Stack trace:');
      }
      // Forcer la mise à jour de la computed property
      this.$forceUpdate();
    },
  },
  async created() {
    this.updateLastUpdate();
    await this.loadFilterOptions();
    await this.getCampagneEnCours();
    // Initialiser kpis avec un objet vide pour permettre l'affichage des KPIs
    // Les KPIs sont maintenant calculés via les computed properties depuis les props
    this.kpis = {};
    // quantiteCollecteeFilteredValue reste null jusqu'à ce que des filtres soient appliqués
  },
  mounted() {
    this.updateLayoutOffsets();
    window.addEventListener('resize', this.updateLayoutOffsets);
    // Écouter les événements de rafraîchissement des filtres
    this.$root.$on('kpi-filters-refresh', this.handleFiltersRefresh);
    // Écouter les événements pour ouvrir la modal depuis KpiFilterPanel
    this.$root.$on('open-kpi-modal', this.handleOpenKpiModal);
    // Écouter les mises à jour des KPIs depuis KpiFilterPanel
    // IMPORTANT: Ces mises à jour seront ignorées après le chargement initial
    this.$root.$on('kpi-data-updated', this.handleKpiDataUpdate);

    // Marquer les données initiales comme chargées après que le composant soit monté
    // Cela garantit que les KPIs principaux ne seront plus modifiés par les filtres
    this.$nextTick(() => {
      // Attendre un court délai pour s'assurer que toutes les props sont chargées depuis index.vue
      setTimeout(() => {
        this.initialDataLoaded = true;
      }, 500);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateLayoutOffsets);
    this.$root.$off('kpi-filters-refresh', this.handleFiltersRefresh);
    this.$root.$off('open-kpi-modal', this.handleOpenKpiModal);
    this.$root.$off('kpi-data-updated', this.handleKpiDataUpdate);
    this.fetchData();
  },
  methods: {
    ...mapActions('dashboard', ['loadFilterOptions', 'updateFilter', 'loadProducteurs']),
    handleKpiDataUpdate(kpis) {
      // IMPORTANT: Ne pas mettre à jour les KPIs principaux après le chargement initial
      // Les KPIs principaux (KpiPanel) doivent rester inchangés une fois chargés depuis l'accueil
      // Les filtres dans KpiFilterPanel ne doivent affecter que la section de filtres, pas les KPIs principaux
      if (this.initialDataLoaded) {
        // Ignorer les mises à jour des filtres une fois les données initiales chargées
        return;
      }
      // Mettre à jour les KPIs depuis KpiFilterPanel (backend) uniquement avant le chargement initial
      // Ceci ne devrait normalement pas se produire, mais on le garde pour la sécurité
      if (kpis && Object.keys(kpis).length > 0) {
        this.filteredKpis = {
          totalParcelles: kpis.totalParcelles || 0,
          totalParcellesGeolocalisees: kpis.totalParcellesGeolocalisees || 0,
          totalProducteurs: kpis.totalProducteurs || 0,
          totalControleurs: kpis.totalControleurs || 0,
          totalGroupements: kpis.totalGroupements || 0,
          totalAntennes: kpis.totalAntennes || 0,
          totalAudits: kpis.totalAudits || 0,
          totalControlesInternes: kpis.totalControlesInternes || 0,
          totalQuantiteCollectee: kpis.totalQuantiteCollectee || 0,
        };
        // Mettre à jour la quantité collectée filtrée avec la valeur du backend (déjà en tonnes)
        if (kpis.totalQuantiteCollectee !== undefined && kpis.totalQuantiteCollectee !== null) {
          this.quantiteCollecteeFilteredValue = kpis.totalQuantiteCollectee;
        }
      }
    },
    async handleFiltersRefresh(filters) {
      // Attendre que le store soit mis à jour
      await this.$nextTick();
      // Synchroniser quickFilters avec les filtres passés en paramètre (plus récents) ou du store
      if (filters && filters.niveau) {
        const niveau = filters.niveau;
        this.quickFilters.ciId = niveau.ci_id || null;
        this.quickFilters.antenneId = niveau.antenne_id || null;
        this.quickFilters.groupementId = niveau.groupement_id || null;
        this.quickFilters.producteurId = niveau.producteur_id || null;
      } else if (this.activeFilters && this.activeFilters.niveau) {
        const niveau = this.activeFilters.niveau;
        this.quickFilters.ciId = niveau.ci_id || null;
        this.quickFilters.antenneId = niveau.antenne_id || null;
        this.quickFilters.groupementId = niveau.groupement_id || null;
        this.quickFilters.producteurId = niveau.producteur_id || null;
      }
      if (filters) {
        if (filters.cultures) {
          this.quickFilters.cultures = Array.isArray(filters.cultures) ? [...filters.cultures] : [];
        }
        if (filters.certification) {
          this.quickFilters.certification = filters.certification || 'toutes';
        }
        if (filters.periode) {
          this.localPeriode = { ...filters.periode };
        }
      } else if (this.activeFilters) {
        this.quickFilters.cultures = Array.isArray(this.activeFilters.cultures) ? [...this.activeFilters.cultures] : [];
        this.quickFilters.certification = this.activeFilters.certification || 'toutes';
      }
      if (this.activeFilters && this.activeFilters.periode) {
        this.localPeriode = { ...this.activeFilters.periode };
      }
      // Attendre encore un tick pour que activeFilters soit synchronisé
      await this.$nextTick();
      // Appliquer les filtres
      await this.applyQuickFilters();
    },

    updateLayoutOffsets() {
      this.$nextTick(() => {
        const fixedHeader = document.querySelector('.fixed-header');
        const tagsView = document.querySelector('#tags-view-container');
        const headerHeight = fixedHeader ? fixedHeader.offsetHeight : 0;
        const tagsHeight = tagsView ? tagsView.offsetHeight : 0;
        this.fixedHeaderOffset = headerHeight + tagsHeight;

        const layoutHeader = this.$refs.dashboardLayoutHeader;
        const headerBlockHeight = layoutHeader ? layoutHeader.offsetHeight : 0;
        const extraSpacing = 20;
        this.layoutBodyOffset = headerBlockHeight + extraSpacing;
      });
    },

    handlePeriodeChange() {
      const type = this.localPeriode.type;
      if (type !== 'custom') {
        this.localPeriode.date_debut = null;
        this.localPeriode.date_fin = null;
      }

      this.updateFilter({ filterName: 'periode', value: { ...this.localPeriode }});
      // Recharger les KPIs avec le nouveau filtre de période
      this.loadKPIs(this.activeFilters);
    },

    handleDateChange() {
      this.updateFilter({ filterName: 'periode', value: { ...this.localPeriode }});
      // Recharger les KPIs avec le nouveau filtre de période
      this.loadKPIs(this.activeFilters);
    },

    getISOWeekNumber(dateStr) {
      if (!dateStr) {
        return null;
      }

      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      if ([year, month, day].some(num => Number.isNaN(num))) {
        return null;
      }

      const date = new Date(Date.UTC(year, month - 1, day));
      const dayNum = date.getUTCDay() || 7;
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
      return weekNo;
    },

    getWeekRange(dateStr) {
      const referenceDate = this.parseDate(dateStr) || new Date();
      const dayNum = referenceDate.getUTCDay() || 7;
      const startDate = new Date(referenceDate);
      startDate.setUTCDate(referenceDate.getUTCDate() - (dayNum - 1));
      const endDate = new Date(startDate);
      endDate.setUTCDate(startDate.getUTCDate() + 6);
      return {
        start: this.formatISODate(startDate),
        end: this.formatISODate(endDate),
        weekNumber: this.getISOWeekNumber(this.formatISODate(startDate)),
      };
    },

    formatISODate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
      }
      const year = date.getUTCFullYear();
      const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
      const day = `${date.getUTCDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    parseDate(dateStr) {
      if (!dateStr) {
        return null;
      }
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      if ([year, month, day].some(num => Number.isNaN(num))) {
        return null;
      }
      return new Date(Date.UTC(year, month - 1, day));
    },

    getTodayISODate() {
      const today = new Date();
      return this.formatISODate(new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())));
    },

    getFallbackCampaignYear() {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      return `${currentYear}-${nextYear}`;
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) {
        return '';
      }

      const date = this.parseDate(dateStr);
      if (!date) {
        return '';
      }
      const formatter = new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      return formatter.format(date);
    },

    async getCampagneEnCours() {
      try {
        // Utiliser la campagne du store si elle existe, sinon récupérer la campagne en cours
        const storeCampId = this.$store.getters['camp_agricole/camp_id'] || sessionStorage.getItem('camp_id');
        if (storeCampId) {
          const campIdNum = parseInt(storeCampId, 10);
          this.campagneId = campIdNum;
          await this.updateCampagneInfo(campIdNum);
        } else {
          // Si aucune campagne dans le store, récupérer la campagne en cours
          const { data } = await campagnesResource.sublist('/en_cours');
          this.campagneId = data.id;
          this.campagneInfo = data;
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la campagne en cours:', error);
      }
    },

    async updateCampagneInfo(campId) {
      try {
        // Récupérer les infos de la campagne depuis la liste
        const { data } = await campagnesResource.list();
        const campagne = data.find(c => c.id === campId);
        if (campagne) {
          this.campagneInfo = campagne;
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des infos de la campagne:', error);
      }
    },

    async refreshDashboard() {
      // Les filtres sont maintenant appliqués via les computed properties
      // Plus besoin d'appel API
      this.updateLastUpdate();
    },

    // Méthode pour filtrer localement les données (plus d'appel API)
    loadKPIs(filters) {
      // Les totaux sont maintenant calculés via les computed properties basés sur les données filtrées
      // Cette méthode est maintenue pour compatibilité mais ne fait plus d'appel API
      // Initialiser kpis avec un objet vide pour permettre l'affichage
      if (!this.kpis || Object.keys(this.kpis).length === 0) {
        this.kpis = {};
      }
      this.updateLastUpdate();
    },

    // Méthodes de filtrage des données
    getFilteredUnitesProductions() {
      if (!this.unitesProductions || this.unitesProductions.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      // Filtrer par antenne_id si spécifié (mais comme on filtre les unités de production elles-mêmes, on retourne toutes si aucun filtre)
      if (niveau.antenne_id) {
        return this.unitesProductions.filter(up => up.id === niveau.antenne_id);
      }
      return this.unitesProductions;
    },

    getFilteredGroupements() {
      if (!this.groupements || this.groupements.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      let filtered = [...this.groupements];

      // Filtrer par controleur_id (ci_id) EN PREMIER
      // Cela permet de d'abord identifier les groupements du CI, puis de vérifier s'ils ont des parcelles dans la campagne
      if (niveau.ci_id) {
        filtered = filtered.filter(grp => grp.controleur_id === niveau.ci_id);
      }

      // Filtrer par antenne_id (unite_production_id)
      if (niveau.antenne_id) {
        filtered = filtered.filter(grp => grp.unite_production_id === niveau.antenne_id);
      }

      // Filtrer par groupement_id
      if (niveau.groupement_id) {
        filtered = filtered.filter(grp => grp.id === niveau.groupement_id);
      }

      // IMPORTANT: Filtrer ENSUITE pour ne garder que les groupements qui ont des parcelles dans la campagne active
      // Cela garantit que le filtrage frontend est identique au backend qui utilise cmp_id
      // Mais on le fait APRÈS avoir filtré par CI/Antenne/Groupement pour éviter d'éliminer des groupements valides
      const groupementIdsInCampagne = new Set();
      if (this.parcellesProducteurs && this.parcellesProducteurs.length > 0 && this.producteurs && this.producteurs.length > 0) {
        // Créer un index producteur_id -> groupement_id
        const producteurById = {};
        this.producteurs.forEach(prod => {
          if (prod && prod.id != null) {
            producteurById[Number(prod.id)] = prod;
          }
        });

        // Trouver les groupements qui ont des parcelles dans la campagne
        this.parcellesProducteurs.forEach(pp => {
          if (pp && pp.producteur_id != null) {
            const producteurId = Number(pp.producteur_id);
            const producteur = producteurById[producteurId];
            if (producteur && producteur.groupement_id != null) {
              const groupementId = Number(producteur.groupement_id);
              if (!isNaN(groupementId)) {
                groupementIdsInCampagne.add(groupementId);
              }
            } else {
              // Debug: log si producteur non trouvé
              if (niveau.ci_id && !producteur) {
                console.warn(`[KpiPanel] Producteur ID ${producteurId} non trouvé dans this.producteurs`);
              }
            }
          }
        });
      }

      // Debug: log pour comprendre le filtrage
      if (niveau.ci_id) {
        console.log(`[KpiPanel] Filtrage par CI ID ${niveau.ci_id}:`, {
          groupementsAvantFiltreCampagne: filtered.length,
          groupementsIdsAvantFiltre: filtered.map(g => g.id),
          groupementIdsInCampagne: Array.from(groupementIdsInCampagne),
          parcellesProducteursCount: this.parcellesProducteurs?.length || 0,
          producteursCount: this.producteurs?.length || 0,
        });
      }

      // Filtrer les groupements pour ne garder que ceux de la campagne active
      // Si aucun filtre de niveau n'est actif (pas de CI, Antenne, Groupement), on filtre par campagne
      // Si un filtre de niveau est actif, on filtre aussi par campagne pour garantir la cohérence avec le backend
      // MAIS: si aucun groupement n'est trouvé après filtrage par campagne ET qu'un filtre CI est actif,
      // c'est peut-être que les données parcelle_producteur ne sont pas à jour - dans ce cas, on garde les groupements filtrés par CI
      if (groupementIdsInCampagne.size > 0) {
        const filteredBefore = filtered.length;
        filtered = filtered.filter(grp => {
          const grpId = Number(grp.id);
          return !isNaN(grpId) && groupementIdsInCampagne.has(grpId);
        });
        if (niveau.ci_id && filtered.length === 0 && filteredBefore > 0) {
          console.warn(`[KpiPanel] Aucun groupement trouvé après filtrage par campagne pour CI ${niveau.ci_id}`);
          console.warn('[KpiPanel] Groupements du CI avant filtrage campagne:', filteredBefore);
          console.warn('[KpiPanel] Groupement IDs dans campagne:', Array.from(groupementIdsInCampagne));
        }
      } else if (this.parcellesProducteurs && this.parcellesProducteurs.length === 0) {
        // Si on n'a pas de données parcelle_producteur du tout, on ne peut pas filtrer par campagne
        // Dans ce cas, on garde les groupements filtrés par CI/Antenne/Groupement
        // (fallback pour éviter de perdre toutes les données)
        console.warn('[KpiPanel] Aucune donnée parcelle_producteur disponible, filtrage par campagne ignoré');
      }

      return filtered;
    },

    getFilteredProducteurs() {
      if (!this.producteurs || this.producteurs.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};

      // IMPORTANT: Filtrer d'abord pour ne garder que les producteurs qui ont des parcelles dans la campagne active
      // Cela garantit que le filtrage frontend est identique au backend qui utilise cmp_id
      const producteurIdsInCampagne = new Set();
      if (this.parcellesProducteurs && this.parcellesProducteurs.length > 0) {
        this.parcellesProducteurs.forEach(pp => {
          if (pp.producteur_id != null) {
            producteurIdsInCampagne.add(Number(pp.producteur_id));
          }
        });
      }

      // Filtrer les producteurs pour ne garder que ceux de la campagne active
      let filtered = this.producteurs.filter(prod => {
        if (producteurIdsInCampagne.size === 0) {
          // Si on n'a pas de données parcelle_producteur, on garde tous les producteurs (fallback)
          return true;
        }
        return producteurIdsInCampagne.has(Number(prod.id));
      });

      // Filtrer par producteur_id
      if (niveau.producteur_id) {
        filtered = filtered.filter(prod => prod.id === niveau.producteur_id);
      }

      // Filtrer par groupement_id
      if (niveau.groupement_id) {
        filtered = filtered.filter(prod => prod.groupement_id === niveau.groupement_id);
      }

      return filtered;
    },

    getFilteredControleurs() {
      if (!this.controleurs || this.controleurs.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      let filtered = [...this.controleurs];

      // Filtrer par controleur_id (ci_id)
      if (niveau.ci_id) {
        filtered = filtered.filter(ctrl => ctrl.id === niveau.ci_id);
      }

      return filtered;
    },

    getFilteredParcelles() {
      if (!this.parcelles || this.parcelles.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      const certificationFilter = this.activeFilters?.certification || 'toutes';
      const periode = this.activeFilters?.periode || {};

      // Si une période autre que "campagne" est sélectionnée, on doit filtrer par created_at
      // Dans ce cas, on s'aligne strictement sur le backend qui renvoie nombre_parcelles filtré
      // Si le backend renvoie 0, on retourne un tableau vide
      if (periode.type && periode.type !== 'campagne') {
        const filteredKpisParcelles = this.filteredKpis?.totalParcelles || 0;
        if (filteredKpisParcelles === 0) {
          return [];
        }
      }

      // Créer des index pour des recherches rapides O(1) au lieu de O(n)
      // IMPORTANT: Utiliser parcelle_producteur pour filtrer uniquement les parcelles de la campagne active
      // Cela garantit que le filtrage frontend est identique au backend qui utilise cmp_id
      const producteurIdByParcelleId = {};
      const parcelleIdsInCampagne = new Set(); // Set des IDs de parcelles dans la campagne active
      const parcelleCreatedAtByParcelleId = {}; // Map pour filtrer par created_at

      if (this.parcellesProducteurs && this.parcellesProducteurs.length > 0) {
        this.parcellesProducteurs.forEach(pp => {
          if (pp.parcelle_id != null && pp.producteur_id != null) {
            producteurIdByParcelleId[pp.parcelle_id] = pp.producteur_id;
            parcelleIdsInCampagne.add(Number(pp.parcelle_id));
            // Stocker created_at pour le filtrage par période
            if (pp.created_at) {
              parcelleCreatedAtByParcelleId[pp.parcelle_id] = pp.created_at;
            }
          }
        });
      }

      // Filtrer d'abord les parcelles pour ne garder que celles de la campagne active
      // C'est crucial pour que le filtrage soit identique au backend
      let filtered = this.parcelles.filter(p => {
        const parcelleId = Number(p.id || (p.parcelle && p.parcelle.id));
        return parcelleIdsInCampagne.has(parcelleId);
      });

      // Filtrer par période (created_at) si une période est sélectionnée
      if (periode.type && periode.type !== 'campagne' && (periode.date_debut || periode.date_fin)) {
        const normalizeStart = (value) => {
          if (!value) {
            return null;
          }
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return null;
          }
          date.setHours(0, 0, 0, 0);
          return date;
        };

        const normalizeEnd = (value) => {
          if (!value) {
            return null;
          }
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return null;
          }
          date.setHours(23, 59, 59, 999);
          return date;
        };

        const startDate = normalizeStart(periode.date_debut || periode.date_fin);
        const endDate = normalizeEnd(periode.date_fin || periode.date_debut);

        filtered = filtered.filter(p => {
          const parcelleId = Number(p.id || (p.parcelle && p.parcelle.id));
          const createdAt = parcelleCreatedAtByParcelleId[parcelleId];
          if (!createdAt) {
            return false;
          }

          const createdDate = new Date(createdAt);
          if (isNaN(createdDate.getTime())) {
            return false;
          }

          if (startDate && createdDate < startDate) {
            return false;
          }
          if (endDate && createdDate > endDate) {
            return false;
          }
          return true;
        });
      }

      // Si aucun filtre de niveau n'est actif et certification est "toutes", retourner les parcelles filtrées par campagne
      if (!niveau.producteur_id && !niveau.groupement_id && !niveau.ci_id && !niveau.antenne_id && certificationFilter === 'toutes') {
        return filtered;
      }

      const producteurById = {};
      if (this.producteurs && this.producteurs.length > 0) {
        this.producteurs.forEach(prod => {
          if (prod.id != null) {
            producteurById[prod.id] = prod;
          }
        });
      }

      // Filtrer par producteur_id
      if (niveau.producteur_id) {
        const parcelleIds = new Set();
        Object.keys(producteurIdByParcelleId).forEach(parcelleId => {
          if (producteurIdByParcelleId[parcelleId] === niveau.producteur_id) {
            parcelleIds.add(Number(parcelleId));
          }
        });
        filtered = filtered.filter(p => parcelleIds.has(Number(p.id)));
      }

      // Filtrer par groupement_id
      if (niveau.groupement_id) {
        const groupementIdNum = Number(niveau.groupement_id);
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && Number(producteur.groupement_id) === groupementIdNum;
          }
          return false;
        });
      }

      // Filtrer par controleur_id (ci_id) via les groupements
      if (niveau.ci_id) {
        const ciIdNum = Number(niveau.ci_id);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.controleur_id != null && Number(g.controleur_id) === ciIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
          }
          return false;
        });
      }

      // Filtrer par antenne_id (unite_production_id) via les groupements
      if (niveau.antenne_id) {
        const antenneIdNum = Number(niveau.antenne_id);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.unite_production_id != null && Number(g.unite_production_id) === antenneIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
          }
          return false;
        });
      }

      // Filtrer par certification
      if (certificationFilter && certificationFilter !== 'toutes') {
        filtered = filtered.filter(p => {
          const statut = this.getParcelleStatut(p);
          if (certificationFilter === 'bio' || certificationFilter === 'AB') {
            return statut === 'AB';
          } else if (certificationFilter === 'non-bio') {
            return statut !== 'AB';
          }
          return true;
        });
      }

      return filtered;
    },

    getParcellesFilteredByQuickFilters() {
      // Méthode helper pour filtrer les parcelles directement avec quickFilters
      // Utile quand activeFilters n'est pas encore synchronisé
      if (!this.parcelles || this.parcelles.length === 0) {
        return [];
      }

      // Créer des index pour des recherches rapides
      // IMPORTANT: Utiliser parcelle_producteur pour filtrer uniquement les parcelles de la campagne active
      const producteurIdByParcelleId = {};
      const parcelleIdsInCampagne = new Set(); // Set des IDs de parcelles dans la campagne active
      if (this.parcellesProducteurs && this.parcellesProducteurs.length > 0) {
        this.parcellesProducteurs.forEach(pp => {
          if (pp.parcelle_id != null && pp.producteur_id != null) {
            producteurIdByParcelleId[pp.parcelle_id] = pp.producteur_id;
            parcelleIdsInCampagne.add(Number(pp.parcelle_id));
          }
        });
      }

      // Filtrer d'abord les parcelles pour ne garder que celles de la campagne active
      // C'est crucial pour que le filtrage soit identique au backend
      let filtered = this.parcelles.filter(p => {
        const parcelleId = Number(p.id || (p.parcelle && p.parcelle.id));
        return parcelleIdsInCampagne.has(parcelleId);
      });

      const producteurById = {};
      if (this.producteurs && this.producteurs.length > 0) {
        this.producteurs.forEach(prod => {
          if (prod.id != null) {
            producteurById[prod.id] = prod;
          }
        });
      }

      // Filtrer par producteur_id
      if (this.quickFilters.producteurId) {
        const parcelleIds = new Set();
        Object.keys(producteurIdByParcelleId).forEach(parcelleId => {
          if (Number(producteurIdByParcelleId[parcelleId]) === Number(this.quickFilters.producteurId)) {
            parcelleIds.add(Number(parcelleId));
          }
        });
        filtered = filtered.filter(p => parcelleIds.has(Number(p.id)));
      }

      // Filtrer par groupement_id
      if (this.quickFilters.groupementId) {
        const groupementIdNum = Number(this.quickFilters.groupementId);
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && Number(producteur.groupement_id) === groupementIdNum;
          }
          return false;
        });
      }

      // Filtrer par controleur_id (ci_id) via les groupements
      if (this.quickFilters.ciId) {
        const ciIdNum = Number(this.quickFilters.ciId);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.controleur_id != null && Number(g.controleur_id) === ciIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
          }
          return false;
        });
      }

      // Filtrer par antenne_id (unite_production_id) via les groupements
      if (this.quickFilters.antenneId) {
        const antenneIdNum = Number(this.quickFilters.antenneId);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.unite_production_id != null && Number(g.unite_production_id) === antenneIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(p => {
          const producteurId = producteurIdByParcelleId[p.id];
          if (producteurId != null) {
            const producteur = producteurById[producteurId];
            return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
          }
          return false;
        });
      }

      // Filtrer par certification
      if (this.quickFilters.certification && this.quickFilters.certification !== 'toutes') {
        filtered = filtered.filter(p => {
          const statut = this.getParcelleStatut(p);
          if (this.quickFilters.certification === 'bio' || this.quickFilters.certification === 'AB') {
            return statut === 'AB';
          } else if (this.quickFilters.certification === 'non-bio') {
            return statut !== 'AB';
          }
          return true;
        });
      }

      return filtered;
    },

    getFilteredAudits() {
      if (!this.audits || this.audits.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      let filtered = [...this.audits];

      // Filtrer par controleur_id (ci_id)
      if (niveau.ci_id) {
        filtered = filtered.filter(audit => audit.controleur_id === niveau.ci_id);
      }

      // Filtrer par zone_id (antenne) si disponible
      if (niveau.antenne_id) {
        filtered = filtered.filter(audit => audit.zone_id === niveau.antenne_id);
      }

      // Filtrer par campagne_id
      if (this.campagneId || this.camp_id) {
        const campId = this.campagneId || this.camp_id;
        filtered = filtered.filter(audit => audit.campagne_agricole_id === campId);
      }

      return filtered;
    },

    getFilteredControlesInternes() {
      if (!this.controlesInternes || this.controlesInternes.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      let filtered = [...this.controlesInternes];

      // Filtrer par campagne
      if (this.campagneId || this.camp_id) {
        const campId = this.campagneId || this.camp_id;
        filtered = filtered.filter(ci => ci.campagne_agricole_id === campId);
      }

      // Si aucun filtre de niveau n'est actif, retourner les contrôles internes filtrés par campagne
      if (!niveau.producteur_id && !niveau.groupement_id && !niveau.ci_id && !niveau.antenne_id) {
        return filtered;
      }

      // Créer des index pour des recherches rapides O(1) au lieu de O(n)
      const producteurIdByParcelleId = {};
      if (this.parcellesProducteurs && this.parcellesProducteurs.length > 0) {
        this.parcellesProducteurs.forEach(pp => {
          if (pp.parcelle_id != null && pp.producteur_id != null) {
            producteurIdByParcelleId[pp.parcelle_id] = pp.producteur_id;
          }
        });
      }

      const producteurById = {};
      if (this.producteurs && this.producteurs.length > 0) {
        this.producteurs.forEach(prod => {
          if (prod.id != null) {
            producteurById[prod.id] = prod;
          }
        });
      }

      // Filtrer par producteur_id via parcelle_id
      if (niveau.producteur_id) {
        const producteurIdNum = Number(niveau.producteur_id);
        const parcelleIds = new Set();
        Object.keys(producteurIdByParcelleId).forEach(parcelleId => {
          if (Number(producteurIdByParcelleId[parcelleId]) === producteurIdNum) {
            parcelleIds.add(Number(parcelleId));
          }
        });
        filtered = filtered.filter(ci => {
          const parcelleId = ci.parcelle_id ? Number(ci.parcelle_id) : null;
          return parcelleId != null && parcelleIds.has(parcelleId);
        });
      }

      // Filtrer par groupement_id via parcelle_id → producteur → groupement_id
      if (niveau.groupement_id) {
        const groupementIdNum = Number(niveau.groupement_id);
        filtered = filtered.filter(ci => {
          const parcelleId = ci.parcelle_id ? Number(ci.parcelle_id) : null;
          if (parcelleId == null) {
            return false;
          }
          const producteurId = producteurIdByParcelleId[parcelleId];
          if (producteurId == null) {
            return false;
          }
          const producteur = producteurById[producteurId];
          return producteur && producteur.groupement_id != null && Number(producteur.groupement_id) === groupementIdNum;
        });
      }

      // Filtrer par controleur_id (ci_id) via parcelle_id → producteur → groupement → controleur_id
      if (niveau.ci_id) {
        const ciIdNum = Number(niveau.ci_id);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.controleur_id != null && Number(g.controleur_id) === ciIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(ci => {
          const parcelleId = ci.parcelle_id ? Number(ci.parcelle_id) : null;
          if (parcelleId == null) {
            return false;
          }
          const producteurId = producteurIdByParcelleId[parcelleId];
          if (producteurId == null) {
            return false;
          }
          const producteur = producteurById[producteurId];
          return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
        });
      }

      // Filtrer par antenne_id (unite_production_id) via parcelle_id → producteur → groupement → unite_production_id
      if (niveau.antenne_id) {
        const antenneIdNum = Number(niveau.antenne_id);
        const groupementIds = new Set();
        if (this.groupements && this.groupements.length > 0) {
          this.groupements.forEach(g => {
            if (g.unite_production_id != null && Number(g.unite_production_id) === antenneIdNum) {
              groupementIds.add(Number(g.id));
            }
          });
        }
        filtered = filtered.filter(ci => {
          const parcelleId = ci.parcelle_id ? Number(ci.parcelle_id) : null;
          if (parcelleId == null) {
            return false;
          }
          const producteurId = producteurIdByParcelleId[parcelleId];
          if (producteurId == null) {
            return false;
          }
          const producteur = producteurById[producteurId];
          return producteur && producteur.groupement_id != null && groupementIds.has(Number(producteur.groupement_id));
        });
      }

      return filtered;
    },

    getFilteredFormations() {
      if (!this.formations || this.formations.length === 0) {
        return [];
      }
      const niveau = this.activeFilters?.niveau || {};
      let filtered = [...this.formations];

      // Filtrer par controleur_id (ci_id)
      if (niveau.ci_id) {
        filtered = filtered.filter(formation => formation.controleur_id === niveau.ci_id);
      }

      // Filtrer par campagne_id
      if (this.campagneId || this.camp_id) {
        const campId = this.campagneId || this.camp_id;
        filtered = filtered.filter(formation => formation.campagne_agricole_id === campId);
      }

      return filtered;
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') {
        return '0';
      }
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) {
        return '0';
      }
      return numValue.toLocaleString('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    updateLastUpdate() {
      const now = new Date();
      this.lastUpdate = now.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    syncQuickFilters(filters) {
      if (!filters) {
        return;
      }
      const niveau = filters.niveau || {};
      const niveauType = niveau.type || 'total';
      const niveauId = niveau.id || null;

      // Utiliser les valeurs individuelles de niveau si elles existent, sinon utiliser le niveau principal
      // Si aucune valeur n'existe dans les filtres, préserver les valeurs existantes dans quickFilters
      if (niveau.ci_id !== undefined && niveau.ci_id !== null) {
        this.quickFilters.ciId = niveau.ci_id;
      } else if (niveauType === 'ci' && niveauId !== null) {
        this.quickFilters.ciId = niveauId;
      } else if (niveau.ci_id === null) {
        // Si explicitement null dans les filtres, réinitialiser
        this.quickFilters.ciId = null;
      }

      if (niveau.antenne_id !== undefined && niveau.antenne_id !== null) {
        this.quickFilters.antenneId = niveau.antenne_id;
      } else if (niveauType === 'antenne' && niveauId !== null) {
        this.quickFilters.antenneId = niveauId;
      } else if (niveau.antenne_id === null) {
        this.quickFilters.antenneId = null;
      }

      if (niveau.groupement_id !== undefined && niveau.groupement_id !== null) {
        this.quickFilters.groupementId = niveau.groupement_id;
      } else if (niveauType === 'groupement' && niveauId !== null) {
        this.quickFilters.groupementId = niveauId;
      } else if (niveau.groupement_id === null) {
        this.quickFilters.groupementId = null;
      }

      if (niveau.producteur_id !== undefined && niveau.producteur_id !== null) {
        this.quickFilters.producteurId = niveau.producteur_id;
      } else if (niveauType === 'producteur' && niveauId !== null) {
        this.quickFilters.producteurId = niveauId;
      } else if (niveau.producteur_id === null) {
        this.quickFilters.producteurId = null;
      }

      // Synchroniser les cultures uniquement si elles sont vraiment différentes
      // Cela évite d'écraser les modifications locales de l'utilisateur
      const filtersCultures = Array.isArray(filters.cultures) ? filters.cultures : [];
      const currentCultures = Array.isArray(this.quickFilters.cultures) ? this.quickFilters.cultures : [];

      // Convertir en sets pour comparer sans tenir compte de l'ordre
      const filtersSet = new Set(filtersCultures.map(id => String(id)));
      const currentSet = new Set(currentCultures.map(id => String(id)));

      // Vérifier si les sets sont différents
      const areEqual = filtersSet.size === currentSet.size &&
          [...filtersSet].every(id => currentSet.has(id));

      // Ne synchroniser que si les cultures sont réellement différentes
      if (!areEqual) {
        this.quickFilters.cultures = [...filtersCultures];
      }

      this.quickFilters.certification = filters.certification || 'toutes';

      if (this.quickFilters.producteurId && this.producteurOptions.length === 0) {
        this.loadProducteursOptions();
      }
    },
    getNiveauOptionsByType(type) {
      return this.filterOptions?.niveaux?.[type]?.options || [];
    },
    getValidAntenneIdsForCI(ciId) {
      if (!ciId) {
        return [];
      }
      const groupements = this.getNiveauOptionsByType('groupement');
      const groupementsDuCI = groupements.filter(
        grp => grp.controleur_id === ciId
      );
      return [...new Set(
        groupementsDuCI
          .map(grp => grp.unite_production_id)
          .filter(Boolean)
      )];
    },
    getValidGroupementIdsForCI(ciId) {
      if (!ciId) {
        return [];
      }
      const allGroupements = this.getNiveauOptionsByType('groupement');
      return allGroupements
        .filter(grp => grp.controleur_id === ciId)
        .map(grp => grp.id);
    },
    getValidGroupementIds(antenneId) {
      if (!antenneId) {
        return [];
      }
      const allGroupements = this.getNiveauOptionsByType('groupement');
      let filtered = allGroupements;

      // Si un CI est sélectionné, filtrer les groupements de ce CI
      if (this.quickFilters.ciId) {
        filtered = filtered.filter(
          grp => grp.controleur_id === this.quickFilters.ciId
        );
      }

      // Filtrer par antenne
      filtered = filtered.filter(
        grp => grp.unite_production_id === antenneId
      );

      return filtered.map(grp => grp.id);
    },
    formatNiveauLabel(option) {
      if (!option) {
        return '';
      }
      return option.nom || option.label || option.libelle || option.name || option.code || `Option ${option.id}`;
    },
    formatProducteurLabel(option) {
      if (!option) {
        return '';
      }
      const fullName = [option.nom, option.prenom].filter(Boolean).join(' ');
      return fullName.trim() || option.code || `Producteur ${option.id}`;
    },
    handleQuickNiveauChange(type, value) {
      const hasValue = Boolean(value);

      // Si on sélectionne une antenne, garder le CI sélectionné
      if (type === 'antenne' && hasValue && this.quickFilters.ciId) {
        // Garder le CI, juste mettre à jour l'antenne
        this.quickFilters.antenneId = value;
        return;
      }

      // Si on désélectionne une antenne mais qu'un CI est sélectionné, garder le CI
      if (type === 'antenne' && !hasValue && this.quickFilters.ciId) {
        this.quickFilters.antenneId = null;
        return;
      }

      // Pour les autres types (CI, groupement, producteur), comportement normal
      if (!hasValue) {
        // Réinitialiser les filtres dépendants seulement si on désélectionne
        if (type === 'ci') {
          this.quickFilters.antenneId = null;
          this.quickFilters.groupementId = null;
          this.quickFilters.producteurId = null;
        } else if (type === 'antenne') {
          this.quickFilters.groupementId = null;
          this.quickFilters.producteurId = null;
        } else if (type === 'groupement') {
          this.quickFilters.producteurId = null;
        }
      }
    },
    findNiveauLabelByType(type, id) {
      if (!id) {
        return null;
      }
      if (type === 'producteur') {
        const option = this.producteurOptions.find(opt => opt.id === id) ||
            (this.filterOptions?.niveaux?.producteur?.options || []).find(opt => opt.id === id);
        return this.formatProducteurLabel(option);
      }
      const option = this.getNiveauOptionsByType(type).find(opt => opt.id === id);
      return this.formatNiveauLabel(option);
    },
    handleQuickCulturesChange(value) {
      // Mettre à jour la valeur locale immédiatement
      const newCultures = Array.isArray(value) ? [...value] : [];
      this.quickFilters.cultures = newCultures;

      // Mettre à jour le store immédiatement pour éviter que syncQuickFilters restaure les anciennes cultures
      this.updateFilter({
        filterName: 'cultures',
        value: [...newCultures],
      });
    },
    async handleProducteurDropdown(visible) {
      if (visible) {
        await this.loadProducteursOptions();
      }
    },
    async loadProducteursOptions() {
      if (this.loadingProducteurs) {
        return;
      }
      this.loadingProducteurs = true;
      try {
        const params = {};
        if (this.quickFilters.groupementId) {
          params.groupement_id = this.quickFilters.groupementId;
        } else if (this.quickFilters.antenneId) {
          params.antenne_id = this.quickFilters.antenneId;
        }
        const producteurs = await this.loadProducteurs(params);
        this.producteurOptions = Array.isArray(producteurs) ? producteurs : [];
      } catch (error) {
        console.error('Erreur lors du chargement des producteurs:', error);
        this.producteurOptions = [];
      } finally {
        this.loadingProducteurs = false;
      }
    },
    getQuantiteCollecteeDisplay() {
      // IMPORTANT: Les KPIs principaux doivent toujours utiliser la valeur globale (prop)
      // et ne jamais être affectés par les filtres de KpiFilterPanel
      // Utiliser uniquement la valeur globale (prop) qui vient de l'accueil
      return this.quantiteCollectee || 0;
    },
    async loadQuantiteCollecteeFiltree() {
      console.log('[Quantité Collectée] loadQuantiteCollecteeFiltree() appelée - Valeur actuelle:', this.quantiteCollecteeFilteredValue);
      if (this.loadingQuantiteCollectee) {
        console.log('[Quantité Collectée] Déjà en cours de chargement, abandon');
        return;
      }

      // Obtenir les parcelles filtrées - si filteredParcelles est vide mais que quickFilters a des filtres,
      // filtrer manuellement les parcelles pour être sûr d'avoir les bonnes données
      let filteredParcelles = this.filteredParcelles || [];
      const allParcelles = this.parcelles || [];

      // Vérifier dans activeFilters (store Vuex)
      const hasActiveFilters = this.activeFilters && (
        this.activeFilters.niveau?.producteur_id ||
        this.activeFilters.niveau?.groupement_id ||
        this.activeFilters.niveau?.ci_id ||
        this.activeFilters.niveau?.antenne_id ||
        (this.activeFilters.certification && this.activeFilters.certification !== 'toutes')
      );

      // Vérifier aussi dans quickFilters (filtres locaux) car activeFilters pourrait ne pas être encore mis à jour
      const hasQuickFilters = this.quickFilters && (
        this.quickFilters.producteurId ||
        this.quickFilters.groupementId ||
        this.quickFilters.ciId ||
        this.quickFilters.antenneId ||
        (this.quickFilters.certification && this.quickFilters.certification !== 'toutes')
      );

      // Si filteredParcelles est vide mais qu'on a des filtres dans quickFilters, filtrer manuellement
      if (filteredParcelles.length === 0 && hasQuickFilters && allParcelles.length > 0) {
        console.log('[Quantité Collectée] filteredParcelles est vide mais quickFilters a des filtres, filtrage manuel...');
        filteredParcelles = this.getParcellesFilteredByQuickFilters();
        console.log('[Quantité Collectée] Parcelles filtrées manuellement:', filteredParcelles.length);
      }

      const hasDifferentParcelles = filteredParcelles.length !== allParcelles.length;

      console.log('[Quantité Collectée] Vérification filtres - Parcelles filtrées:', filteredParcelles.length, '| Total parcelles:', allParcelles.length);
      console.log('[Quantité Collectée] hasActiveFilters:', hasActiveFilters, '| hasQuickFilters:', hasQuickFilters, '| hasDifferentParcelles:', hasDifferentParcelles);
      console.log('[Quantité Collectée] activeFilters:', this.activeFilters);
      console.log('[Quantité Collectée] quickFilters:', this.quickFilters);

      // Utiliser toutes les vérifications : activeFilters, quickFilters, ou parcelles différentes
      const hasFilters = hasActiveFilters || hasQuickFilters || hasDifferentParcelles;

      if (!hasFilters) {
        // Ne réinitialiser que si la valeur n'a jamais été calculée ou si elle est déjà null
        // Ne pas réinitialiser si une valeur filtrée a déjà été calculée (pour éviter de perdre la valeur)
        if (this.quantiteCollecteeFilteredValue == null) {
          console.log('[Quantité Collectée] Aucun filtre actif et valeur déjà null, pas de changement');
          return;
        }
        console.log('[Quantité Collectée] Aucun filtre actif, mais valeur existe:', this.quantiteCollecteeFilteredValue, '- on garde la valeur');
        // Ne pas réinitialiser pour garder la dernière valeur calculée
        return;
      }

      // console.log('[Quantité Collectée] Filtres détectés, calcul de la quantité filtrée...');

      this.loadingQuantiteCollectee = true;
      try {
        // console.log('[Quantité Collectée] Nombre de parcelles filtrées:', filteredParcelles.length, 'sur', allParcelles.length);

        if (filteredParcelles.length === 0) {
          console.log('[Quantité Collectée] Aucune parcelle filtrée, quantité = 0');
          this.quantiteCollecteeFilteredValue = 0;
          await this.$nextTick();
          return;
        }

        // Extraire les IDs des parcelles filtrées
        const parcelleIds = filteredParcelles.map(p => {
          const id = p.id || (p.parcelle && p.parcelle.id);
          return id != null ? Number(id) : null;
        }).filter(id => id != null);

        // console.log('[Quantité Collectée] IDs des parcelles filtrées:', parcelleIds.slice(0, 10), '... (total:', parcelleIds.length, ')');

        if (parcelleIds.length === 0) {
          console.log('[Quantité Collectée] Aucun ID de parcelle valide, quantité = 0');
          this.quantiteCollecteeFilteredValue = 0;
          await this.$nextTick();
          return;
        }

        // Appeler l'API pour obtenir la quantité collectée filtrée
        const campId = this.campagneId || this.camp_id;
        // console.log('[Quantité Collectée] Appel API avec campagne_id:', campId, 'et', parcelleIds.length, 'parcelles');
        // console.log('[Quantité Collectée] Premiers IDs:', parcelleIds.slice(0, 10));

        const response = await request({
          url: '/parcelles/quantite-collectee-filtree',
          method: 'post',
          data: {
            parcelle_ids: parcelleIds.map(id => Number(id)), // S'assurer que tous les IDs sont des nombres
            campagne_id: campId ? Number(campId) : null,
          },
        });

        console.log('[Quantité Collectée] Réponse API complète:', response);

        // Gérer différents formats de réponse possibles
        // Le backend retourne : {success: true, data: {quantite_collectee: ...}} (en kilogrammes)
        // Après request() : {data: {success: true, data: {quantite_collectee: ...}}}
        let quantiteKg = 0;
        if (response && response.data) {
          // Vérifier si la réponse est wrappée dans data.data
          if (response.data.data && response.data.data.quantite_collectee !== undefined) {
            quantiteKg = Number(response.data.data.quantite_collectee) || 0;
          } else if (response.data.quantite_collectee !== undefined) {
            quantiteKg = Number(response.data.quantite_collectee) || 0;
          }
        }

        // Convertir de kilogrammes en tonnes (1 tonne = 1000 kg)
        const quantiteTonnes = quantiteKg / 1000;

        console.log('[Quantité Collectée] Conversion:', {
          kg: quantiteKg,
          tonnes: quantiteTonnes,
        });

        // Utiliser Vue.set pour garantir la réactivité
        this.$set(this, 'quantiteCollecteeFilteredValue', quantiteTonnes);
        console.log('[Quantité Collectée] Valeur après $set:', this.quantiteCollecteeFilteredValue);
        // Forcer la mise à jour du template
        this.$forceUpdate();
        // Attendre que Vue mette à jour
        await this.$nextTick();
        console.log('[Quantité Collectée] Après $nextTick, valeur:', this.quantiteCollecteeFilteredValue);
        // Vérifier que la méthode getQuantiteCollecteeDisplay voit la nouvelle valeur
        console.log('[Quantité Collectée] Méthode getQuantiteCollecteeDisplay:', this.getQuantiteCollecteeDisplay());
      } catch (error) {
        console.error('[Quantité Collectée] Erreur lors du chargement:', error);
        console.error('[Quantité Collectée] Détails de l\'erreur:', error.response?.data || error.message);
        this.quantiteCollecteeFilteredValue = 0;
      } finally {
        this.loadingQuantiteCollectee = false;
      }
    },
    async applyQuickFilters() {
      // Activer le loading global dès le début
      this.kpisLoading = true;
      // Émettre un événement pour déclencher le loading du parent (index.vue)
      this.$root.$emit('kpi-filters-loading', { loading: true, message: 'Application des filtres...' });
      // Attendre 3 secondes avant de continuer
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        // Déterminer le niveau principal à utiliser (priorité: producteur > groupement > antenne > ci > total)
        let niveauType = 'total';
        let niveauId = null;
        let niveauLabel = null;

        if (this.quickFilters.producteurId) {
          niveauType = 'producteur';
          niveauId = this.quickFilters.producteurId;
          niveauLabel = this.findNiveauLabelByType('producteur', niveauId);
        } else if (this.quickFilters.groupementId) {
          niveauType = 'groupement';
          niveauId = this.quickFilters.groupementId;
          niveauLabel = this.findNiveauLabelByType('groupement', niveauId);
        } else if (this.quickFilters.antenneId) {
          niveauType = 'antenne';
          niveauId = this.quickFilters.antenneId;
          niveauLabel = this.findNiveauLabelByType('antenne', niveauId);
        } else if (this.quickFilters.ciId) {
          niveauType = 'ci';
          niveauId = this.quickFilters.ciId;
          niveauLabel = this.findNiveauLabelByType('ci', niveauId);
        }

        // Mettre à jour les filtres dans le store avec tous les niveaux sélectionnés
        await this.updateFilter({
          filterName: 'niveau',
          value: {
            type: niveauType,
            id: niveauId,
            label: niveauLabel,
            // Inclure tous les filtres de niveau sélectionnés
            ci_id: this.quickFilters.ciId || null,
            antenne_id: this.quickFilters.antenneId || null,
            groupement_id: this.quickFilters.groupementId || null,
            producteur_id: this.quickFilters.producteurId || null,
          },
        });
        await this.updateFilter({
          filterName: 'cultures',
          value: [...this.quickFilters.cultures],
        });
        await this.updateFilter({
          filterName: 'certification',
          value: this.quickFilters.certification || 'toutes',
        });

        // Préparer les filtres avec les valeurs de quickFilters pour garantir la cohérence
        await this.updateFilter({
          filterName: 'cultures',
          value: Array.isArray(this.quickFilters.cultures) ? [...this.quickFilters.cultures] : [],
        });
        await this.updateFilter({
          filterName: 'certification',
          value: this.quickFilters.certification || 'toutes',
        });
        await this.updateFilter({
          filterName: 'niveau',
          value: {
            type: niveauType,
            id: niveauId,
            label: niveauLabel,
            ci_id: this.quickFilters.ciId !== undefined && this.quickFilters.ciId !== null ? this.quickFilters.ciId : null,
            antenne_id: this.quickFilters.antenneId !== undefined && this.quickFilters.antenneId !== null ? this.quickFilters.antenneId : null,
            groupement_id: this.quickFilters.groupementId !== undefined && this.quickFilters.groupementId !== null ? this.quickFilters.groupementId : null,
            producteur_id: this.quickFilters.producteurId !== undefined && this.quickFilters.producteurId !== null ? this.quickFilters.producteurId : null,
          },
        });

        // Les filtres sont maintenant appliqués via les computed properties, plus besoin d'appel API
        this.updateLastUpdate();

        // Attendre que les filtres soient bien mis à jour dans le store
        await this.$nextTick();

        // Attendre un peu plus pour que les computed properties se mettent à jour
        await new Promise(resolve => setTimeout(resolve, 200));

        // Forcer la mise à jour des computed properties
        this.$forceUpdate();
        await this.$nextTick();

        // Ne plus charger la quantité collectée filtrée localement
        // La valeur vient maintenant du backend via l'événement kpi-data-updated
        // await this.loadQuantiteCollecteeFiltree();

        // Émettre un événement pour indiquer que toutes les requêtes KPI sont terminées
        this.$root.$emit('kpi-requests-completed');

        // Calculer les résultats après application des filtres
        const results = {
          filtres_appliques: {
            niveau: {
              type: niveauType,
              id: niveauId,
              label: niveauLabel,
              ci_id: this.quickFilters.ciId || null,
              antenne_id: this.quickFilters.antenneId || null,
              groupement_id: this.quickFilters.groupementId || null,
              producteur_id: this.quickFilters.producteurId || null,
            },
            cultures: this.quickFilters.cultures || [],
            certification: this.quickFilters.certification || 'toutes',
          },
          resultats_kpis: {
            totalParcelles: this.totalParcelles,
            totalParcellesGeolocalisees: this.totalParcellesGeolocalisees,
            totalControleurs: this.totalControleurs,
            totalProducteurs: this.totalProducteurs,
            totalGroupements: this.totalGroupements,
            totalAntennes: this.totalAntennes,
            totalAudits: this.totalAudits,
            totalControlesInternes: this.totalControlesInternes,
            totalFormations: this.totalFormations,
            surfaceCertifiee: this.surfaceCertifieeFiltered,
            surfaceDeclassee: this.surfaceDeclasseeFiltered,
            quantiteCollectee: this.quantiteCollecteeFiltered,
          },
          activeFilters: this.activeFilters,
        };

        // Émettre un événement pour notifier que les filtres ont été appliqués avec les résultats
        // ATTENTION: Cet événement est émis APRÈS que toutes les requêtes KPI soient terminées
        // Les graphiques doivent attendre 'kpi-requests-completed' avant de faire leurs requêtes
        this.$root.$emit('filters-applied', results);

        // Émettre un événement pour mettre à jour les KPIs dans les autres composants
        const quantiteCollectee = this.quantiteCollecteeFilteredValue !== null && this.quantiteCollecteeFilteredValue !== undefined
          ? this.quantiteCollecteeFilteredValue
          : (this.getQuantiteCollecteeDisplay ? this.getQuantiteCollecteeDisplay() : 0);
        this.$root.$emit('kpi-data-updated', {
          totalParcelles: this.totalParcelles || 0,
          totalParcellesGeolocalisees: this.totalParcellesGeolocalisees || 0,
          totalProducteurs: this.totalProducteurs || 0,
          totalControleurs: this.totalControleurs || 0,
          totalGroupements: this.totalGroupements || 0,
          totalAntennes: this.totalAntennes || 0,
          totalAudits: this.totalAudits || 0,
          totalControlesInternes: this.totalControlesInternes || 0,
          totalQuantiteCollectee: quantiteCollectee || 0,
          quantiteCollecteeFilteredValue: this.quantiteCollecteeFilteredValue,
        });

        // Afficher un message de succès avec un résumé
        const summary = [];
        if (niveauLabel) {
          summary.push(niveauLabel);
        }
        if (this.quickFilters.cultures && this.quickFilters.cultures.length > 0) {
          summary.push(`${this.quickFilters.cultures.length} culture(s)`);
        }
        if (this.quickFilters.certification && this.quickFilters.certification !== 'toutes') {
          summary.push(`Certification: ${this.quickFilters.certification}`);
        }
        const summaryText = summary.length > 0 ? ` (${summary.join(', ')})` : '';
        this.$message.success(`Filtres appliqués avec succès${summaryText}`);
      } catch (error) {
        console.error('Erreur lors de l\'application des filtres:', error);
        this.$message.error('Erreur lors de l\'application des filtres');
      } finally {
        // Désactiver le loading
        this.kpisLoading = false;
        // Émettre un événement pour désactiver le loading du parent (index.vue)
        this.$root.$emit('kpi-filters-loading', { loading: false });
      }
    },
    async resetQuickFilters() {
      // Réinitialiser tous les filtres rapides
      this.quickFilters = {
        antenneId: null,
        ciId: null,
        groupementId: null,
        producteurId: null,
        cultures: [],
        certification: 'toutes',
      };

      // Réinitialiser la période locale
      this.localPeriode = {
        type: 'campagne',
        date_debut: null,
        date_fin: null,
      };

      // Réinitialiser les filtres dans le store
      await this.updateFilter({
        filterName: 'periode',
        value: {
          type: 'campagne',
          date_debut: null,
          date_fin: null,
        },
      });
      await this.updateFilter({
        filterName: 'niveau',
        value: { type: 'total', id: null, label: null },
      });
      await this.updateFilter({ filterName: 'cultures', value: [] });
      await this.updateFilter({ filterName: 'certification', value: 'toutes' });

      // Réinitialiser la quantité collectée filtrée à null pour utiliser la valeur globale
      this.quantiteCollecteeFilteredValue = null;

      // Les filtres sont maintenant appliqués via les computed properties
      this.updateLastUpdate();

      // Attendre que les filtres soient bien mis à jour dans le store
      await this.$nextTick();

      // Émettre un événement pour notifier que les filtres ont été réinitialisés
      this.$root.$emit('filters-applied');
    },
    handleOpenKpiModal(data) {
      // Handler pour l'événement open-kpi-modal émis par KpiFilterPanel
      if (data && data.title && data.value !== undefined) {
        this.openKpiModal(data.title, data.value);
      }
    },
    openKpiModal(title, value) {
      // Fermer d'abord si une modal est déjà ouverte pour éviter les conflits
      if (this.kpiModalVisible) {
        this.kpiModalVisible = false;
        this.$nextTick(() => {
          this.openModalWithData(title, value);
        });
      } else {
        this.openModalWithData(title, value);
      }
    },
    openModalWithData(title, value) {
      try {
        // Mettre à jour les données d'abord
        this.selectedKpiTitle = title;
        this.selectedKpiValue = value;
        // Incrémenter la clé pour forcer un nouveau rendu propre
        this.modalKey += 1;

        // Charger les données spécifiques selon le KPI
        if (title === 'Parcelles' || title === 'Parcelles géolocalisées') {
          // Pour les parcelles géolocalisées, réinitialiser le filtre pour ne montrer que les géolocalisées
          if (title === 'Parcelles géolocalisées') {
            this.parcelleGeolocFilter = 'geoloc';
          }
          this.loadParcellesList();
          // Réinitialiser la pagination
          this.parcellePage = 1;
        } else if (title === 'Contrôleurs') {
          this.loadControleursList();
        } else if (title === 'Producteurs') {
          this.loadProducteursList();
        }

        // Ouvrir la modal immédiatement (les données sont déjà dans les props)
        // Utiliser $nextTick au lieu de requestAnimationFrame pour une meilleure intégration avec Vue
        this.$nextTick(() => {
          this.kpiModalVisible = true;
        });
      } catch (error) {
        this.$message.error('Erreur lors de l\'ouverture de la modal');
      }
    },
    closeKpiModal() {
      this.kpiModalVisible = false;
    },
    onModalOpened() {
      // Callback quand la modal est complètement ouverte
      // Peut être utilisé pour des initialisations si nécessaire
    },
    loadParcellesForCount() {
      // Utiliser les données filtrées depuis les props au lieu de faire une requête API
      try {
        // Utiliser les parcelles filtrées depuis les computed properties
        this.parcellesListForCount = [...(this.filteredParcelles || [])];
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles pour le calcul:', error);
        this.parcellesListForCount = [];
      }
    },
    loadControleursList() {
      // Utiliser les données filtrées depuis les props au lieu de faire une requête API
      this.loadingControleurs = true;
      this.controleursList = [];
      try {
        // Utiliser les contrôleurs filtrés depuis les computed properties
        this.controleursList = [...(this.filteredControleurs || [])];

        // Forcer la mise à jour de Vue pour afficher les nombres de producteurs
        this.$forceUpdate();
      } catch (error) {
        console.error('Erreur lors du chargement des contrôleurs:', error);
        this.controleursList = [];
      } finally {
        this.loadingControleurs = false;
      }
    },
    loadProducteursList() {
      // Utiliser les données depuis les props
      this.loadingProducteurs = true;
      this.producteursList = [];
      try {
        // Utiliser directement les producteurs depuis les props
        this.producteursList = Array.isArray(this.producteurs) ? [...this.producteurs] : [];
        // Réinitialiser la pagination au début
        this.producteurPage = 1;
      } catch (error) {
        console.error('Erreur lors du chargement des producteurs:', error);
        this.producteursList = [];
      } finally {
        this.loadingProducteurs = false;
      }
    },
    loadParcellesList() {
      console.log('loadParcellesList appelée');
      // Utiliser les données filtrées depuis les props au lieu de faire une requête API
      this.loadingParcelles = true;
      try {
        console.log('Récupération des parcelles filtrées...');
        // Utiliser directement getFilteredParcelles() pour éviter les problèmes de réactivité
        const filtered = this.getFilteredParcelles();
        console.log('Parcelles filtrées récupérées:', filtered?.length || 0);
        // Assigner directement la référence (pas besoin de copier, on utilise la même référence)
        this.parcellesList = Array.isArray(filtered) ? filtered : [];
        console.log('parcellesList assignée:', this.parcellesList.length);
        // Réinitialiser la pagination au début
        this.parcellePage = 1;
      } catch (error) {
        console.error('Erreur lors du chargement des parcelles:', error);
        console.error('Stack trace:', error.stack);
        this.parcellesList = [];
      } finally {
        // Désactiver le loading de manière synchrone
        this.loadingParcelles = false;
        console.log('loadParcellesList terminée');
      }
    },
    nextParcellePage() {
      if (this.parcellePage < this.parcelleTotalPages) {
        this.parcellePage += 1;
      }
    },
    prevParcellePage() {
      if (this.parcellePage > 1) {
        this.parcellePage -= 1;
      }
    },
    nextProducteurPage() {
      if (this.producteurPage < this.producteurTotalPages) {
        this.producteurPage += 1;
      }
    },
    prevProducteurPage() {
      if (this.producteurPage > 1) {
        this.producteurPage -= 1;
      }
    },
    formatControleurName(controleur) {
      if (!controleur) {
        return 'N/A';
      }
      const nom = controleur.nom || '';
      const prenom = controleur.prenom || '';
      return [nom, prenom].filter(Boolean).join(' ') || 'N/A';
    },
    formatUnitesProduction(controleur) {
      if (!controleur) {
        return 'N/A';
      }

      // DEBUG: tracer les données pour le controleur ADJOLO (id 79)
      try {
        if (controleur.id === 79 || /adjolo/i.test(this.formatControleurName(controleur))) {
          const groupementsControleurDebug = Array.isArray(this.groupements)
            ? this.groupements
              .filter(g => g && g.controleur_id === controleur.id)
              .map(g => ({
                id: g.id,
                nom: g.nom,
                unite_production_id: g.unite_production_id,
              }))
            : [];

          const unitesProductionsDebug = Array.isArray(this.unitesProductions)
            ? this.unitesProductions.map(up => ({
              id: up.id,
              code: up.code,
              nom: up.nom,
              controleur_id: up.controleur_id,
            }))
            : [];

          // eslint-disable-next-line no-console
          console.log('[DEBUG ADJOLO] controleur', {
            id: controleur.id,
            nom: this.formatControleurName(controleur),
            unite_production_field: controleur.unite_production,
            groupements_controleur: groupementsControleurDebug,
            unites_productions: unitesProductionsDebug,
          });
        }
      } catch (e) {
        // Ne pas casser l'affichage en cas d'erreur de debug
      }

      // 1. Priorité : utiliser le champ enrichi unite_production (tableau non vide ou string non vide)
      if (Array.isArray(controleur.unite_production) && controleur.unite_production.length > 0) {
        // Exemple attendu : "PO10, PO11, ..."
        return controleur.unite_production.join(', ');
      }
      if (typeof controleur.unite_production === 'string' && controleur.unite_production.trim() !== '') {
        return controleur.unite_production;
      }

      // 2. Fallback : reconstruire les unités de production à partir
      //    des groupements globaux et des unitesProductions globaux
      if (!Array.isArray(this.groupements) || this.groupements.length === 0 ||
          !Array.isArray(this.unitesProductions) || this.unitesProductions.length === 0) {
        return 'Aucune';
      }

      // Filtrer les groupements de ce contrôleur
      const groupementsControleur = this.groupements.filter(g => g && g.controleur_id === controleur.id);
      if (groupementsControleur.length === 0) {
        return 'Aucune';
      }

      // Construire un set d'IDs d'unités de production
      const uniteIds = new Set();
      groupementsControleur.forEach(g => {
        if (g.unite_production_id != null) {
          uniteIds.add(g.unite_production_id);
        }
      });

      if (uniteIds.size === 0) {
        return 'Aucune';
      }

      // Mapper les IDs aux codes/nom des unitesProductions
      const labels = [];
      const missingIds = [];

      this.unitesProductions.forEach(up => {
        if (up && uniteIds.has(up.id)) {
          const label = up.nom || up.libelle || up.label || up.code;
          if (label && !labels.includes(label)) {
            labels.push(label);
          }
        }
      });

      // Identifier les IDs manquants
      uniteIds.forEach(id => {
        const found = this.unitesProductions.some(up => up && up.id === id);
        if (!found && !this.unitesProductionsCache[id]) {
          missingIds.push(id);
        }
      });

      // Récupérer les unités manquantes depuis le cache ou l'API
      if (missingIds.length > 0) {
        this.loadMissingUnitesProductions(missingIds);
        // Utiliser le cache pour les unités déjà chargées
        missingIds.forEach(id => {
          if (this.unitesProductionsCache[id]) {
            const cached = this.unitesProductionsCache[id];
            const label = cached.nom || cached.libelle || cached.label || cached.code;
            if (label && !labels.includes(label)) {
              labels.push(label);
            }
          }
        });
      } else {
        // Utiliser le cache pour tous les IDs manquants
        uniteIds.forEach(id => {
          if (this.unitesProductionsCache[id]) {
            const cached = this.unitesProductionsCache[id];
            const label = cached.nom || cached.libelle || cached.label || cached.code;
            if (label && !labels.includes(label)) {
              labels.push(label);
            }
          }
        });
      }

      if (labels.length === 0) {
        return 'Aucune';
      }

      labels.sort((a, b) => a.localeCompare(b));
      return labels.join(', ');
    },
    formatNombreGroupements(controleur) {
      if (!controleur) {
        return 0;
      }
      // Si groupements est déjà un nombre (enrichi depuis index.vue), l'utiliser directement
      if (typeof controleur.groupements === 'number') {
        return controleur.groupements;
      }
      // Sinon, si c'est un tableau (rétrocompatibilité), compter les éléments
      const groupements = controleur.groupements || [];
      if (Array.isArray(groupements)) {
        return groupements.length;
      }
      return 0;
    },
    formatGroupements(controleur) {
      if (!controleur) {
        return 'N/A';
      }
      // Les groupements sont directement dans controleur.groupements
      const groupements = controleur.groupements || [];
      if (!Array.isArray(groupements) || groupements.length === 0) {
        return 'Aucun';
      }
      return groupements.map(g => g.nom || g.libelle || g.name || g.code || String(g)).join(', ');
    },
    async loadProducteursCountForControleurs() {
      // Réinitialiser le cache
      this.controleursProducteursCount = {};

      if (!this.controleursList || this.controleursList.length === 0) {
        return;
      }

      // Extraire tous les IDs de contrôleurs
      const controleurIds = this.controleursList
        .map(c => c.id)
        .filter(id => id !== undefined && id !== null);

      if (controleurIds.length === 0) {
        return;
      }

      try {
        // Appeler le nouvel endpoint optimisé qui compte tous les producteurs en une seule fois
        // Utiliser directement request pour POST
        const request = (await import('@/utils/request')).default;
        const response = await request({
          url: '/controleurs/count-producteurs',
          method: 'post',
          data: {
            controleur_ids: controleurIds,
          },
        });

        if (response && response.data) {
          // Le backend retourne un objet { controleur_id: count }
          // Réinitialiser l'objet puis assigner les nouvelles valeurs pour garantir la réactivité
          this.controleursProducteursCount = {};
          Object.assign(this.controleursProducteursCount, response.data);
        }
      } catch (error) {
        console.error('Erreur lors du comptage des producteurs:', error);
        // En cas d'erreur, mettre 0 pour tous les contrôleurs
        controleurIds.forEach(id => {
          this.$set(this.controleursProducteursCount, id, 0);
        });
      }
    },
    async loadMissingUnitesProductions(uniteIds) {
      if (!uniteIds || uniteIds.length === 0) {
        return;
      }

      // Filtrer les IDs qui ne sont pas déjà en cache
      const idsToLoad = uniteIds.filter(id => !this.unitesProductionsCache[id]);

      if (idsToLoad.length === 0) {
        return;
      }

      try {
        // Récupérer chaque unité de production manquante individuellement
        const promises = idsToLoad.map(async(id) => {
          try {
            const response = await uniteProductionResource.get(id);
            if (response && response.data) {
              this.$set(this.unitesProductionsCache, id, response.data);
            }
          } catch (error) {
            console.warn(`Erreur lors du chargement de l'unité de production ${id}:`, error);
          }
        });

        await Promise.all(promises);
      } catch (error) {
        console.error('Erreur lors du chargement des unités de production manquantes:', error);
      }
    },
    formatNombreProducteurs(controleur) {
      if (!controleur || !controleur.id) {
        return 0;
      }

      // Priorité 1: Utiliser le champ enrichi producteurs (nombre) depuis index.vue
      if (typeof controleur.producteurs === 'number') {
        return controleur.producteurs;
      }

      // Priorité 2: Utiliser le cache si disponible
      if (this.controleursProducteursCount[controleur.id] !== undefined) {
        const count = this.controleursProducteursCount[controleur.id];
        return count || 0;
      }

      // Afficher "..." pendant le chargement si la requête est en cours
      // On peut détecter cela en vérifiant si le cache existe mais est vide
      // Pour l'instant, retourner 0 si pas encore chargé

      // Fallback: essayer depuis les données des groupements si disponible
      const groupements = controleur.groupements || [];
      if (!Array.isArray(groupements) || groupements.length === 0) {
        return 0;
      }

      // Compter depuis producteurs_data si disponible
      const producteursIds = new Set();
      groupements.forEach(groupement => {
        if (!groupement) {
          return;
        }
        if (Array.isArray(groupement.producteurs_data) && groupement.producteurs_data.length > 0) {
          groupement.producteurs_data.forEach(prod => {
            if (prod && prod.id) {
              producteursIds.add(prod.id);
            }
          });
        }
      });

      if (producteursIds.size > 0) {
        return producteursIds.size;
      }

      // Dernier fallback: additionner les nombres
      let total = 0;
      groupements.forEach(groupement => {
        if (typeof groupement.producteurs === 'number' && groupement.producteurs > 0) {
          total += groupement.producteurs;
        }
      });

      return total;
    },
    formatProducteurNumero(producteur) {
      if (!producteur) {
        return 'N/A';
      }
      // Vérifier numero uniquement
      if (producteur.numero) {
        const numero = String(producteur.numero).trim();
        if (numero) {
          return numero;
        }
      }
      return 'N/A';
    },
    formatProducteurCode(producteur) {
      if (!producteur) {
        return 'N/A';
      }
      // Vérifier code uniquement
      if (producteur.code) {
        const code = String(producteur.code).trim();
        if (code) {
          return code;
        }
      }
      return 'N/A';
    },
    formatProducteurName(producteur) {
      if (!producteur) {
        return 'N/A';
      }
      if (typeof producteur === 'string') {
        return producteur;
      }
      const nom = producteur.nom || '';
      const prenom = producteur.prenom || '';
      return [nom, prenom].filter(Boolean).join(' ') || 'N/A';
    },
    getParcelleProducteurName(parcelle) {
      // 1. Récupérer l'id de la parcelle
      const parcelleId =
        parcelle.id ||
        parcelle.parcelle_id ||
        (parcelle.parcelle && parcelle.parcelle.id);

      if (!parcelleId) {
        // Fallback sur les données éventuellement déjà présentes dans la ligne
        return this.formatProducteurName(parcelle.producteur || parcelle.producteur_data);
      }

      // 2. Récupérer l'id du producteur via la map pré-calculée
      const producteurId = this.parcelleProducteurByParcelleId[parcelleId];
      if (!producteurId) {
        return this.formatProducteurName(parcelle.producteur || parcelle.producteur_data);
      }

      // 3. Récupérer le producteur via la map producteurById
      const producteur = this.producteurById[producteurId];
      if (!producteur) {
        return this.formatProducteurName(parcelle.producteur || parcelle.producteur_data);
      }

      // 4. Retourner le nom formaté
      return this.formatProducteurName(producteur);
    },
    handleExportControleurs(format) {
      if (!format) {
        return;
      }

      const dataToExport = this.filteredControleursList;

      if (dataToExport.length === 0) {
        this.$message.warning('Aucune donnée à exporter');
        this.controleurExportFormat = null;
        return;
      }

      if (format === 'excel') {
        this.exportControleursToExcel(dataToExport);
      } else if (format === 'pdf') {
        this.exportControleursToPDF(dataToExport);
      }

      // Réinitialiser le select après l'export
      this.controleurExportFormat = null;
    },
    exportControleursToExcel(data) {
      try {
        const header = [
          'Nom',
          'Unités de production',
          'Groupements',
          'Nombre de producteurs',
        ];

        const exportData = data.map(controleur => {
          const nom = this.formatControleurName(controleur);
          const unites = this.formatUnitesProduction(controleur);
          const nbGroupements = this.formatNombreGroupements(controleur);
          const nbProducteurs = this.formatNombreProducteurs(controleur);

          return [nom, unites, nbGroupements, nbProducteurs];
        });

        const filename = `controleurs_${this.campagneDisplayYear || new Date().getFullYear()}_${new Date().toISOString().split('T')[0]}`;

        export_json_to_excel({
          header,
          data: exportData,
          filename,
          autoWidth: true,
          bookType: 'xlsx',
        });

        this.$message.success('Export Excel réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export Excel:', error);
        this.$message.error('Erreur lors de l\'export Excel');
      }
    },
    exportControleursToPDF(data) {
      try {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
        });

        // Titre
        doc.setFontSize(16);
        doc.text('Liste des Contrôleurs', 14, 15);

        // Informations de la campagne
        doc.setFontSize(10);
        doc.text(`Campagne: ${this.campagneDisplayYear || 'N/A'}`, 14, 22);
        doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, 14, 27);
        doc.text(`Nombre de contrôleurs: ${data.length}`, 14, 32);

        // Préparer les données pour le tableau
        const tableData = data.map(controleur => {
          const nom = this.formatControleurName(controleur);
          const unites = this.formatUnitesProduction(controleur);
          const nbGroupements = this.formatNombreGroupements(controleur);
          const nbProducteurs = this.formatNombreProducteurs(controleur);

          return [nom, unites, nbGroupements.toString(), nbProducteurs.toString()];
        });

        const headers = [
          ['Nom', 'Unités de production', 'Groupements', 'Nombre de producteurs'],
        ];

        // Utiliser autoTable si disponible, sinon créer un tableau simple
        if (doc.autoTable) {
          doc.autoTable({
            head: headers,
            body: tableData,
            startY: 38,
            styles: { fontSize: 7 },
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 60 },
              2: { cellWidth: 80 },
              3: { cellWidth: 40 },
            },
            margin: { left: 14, right: 14 },
          });
        } else {
          // Fallback: tableau simple sans autoTable
          let yPos = 40;
          doc.setFontSize(7);

          // En-têtes avec fond gris
          doc.setFillColor(200, 200, 200);
          doc.rect(14, yPos - 5, 266, 6, 'F');
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');

          const headerX = [18, 80, 160, 260];
          headers[0].forEach((header, index) => {
            const text = String(header).substring(0, index === 2 ? 20 : 15);
            doc.text(text, headerX[index], yPos);
          });

          doc.setFont('helvetica', 'normal');
          yPos += 7;

          // Ligne de séparation
          doc.setDrawColor(200, 200, 200);
          doc.line(14, yPos, 280, yPos);
          yPos += 4;

          // Données avec gestion de plusieurs pages
          let rowCount = 0;
          tableData.forEach((row) => {
            // Vérifier si on doit créer une nouvelle page
            if (yPos > 185) {
              try {
                doc.addPage({
                  orientation: 'landscape',
                  format: 'a4',
                });
              } catch (e) {
                doc.addPage('landscape');
              }
              yPos = 15;

              // Réimprimer les en-têtes
              doc.setFillColor(200, 200, 200);
              doc.rect(14, yPos - 5, 266, 6, 'F');
              doc.setFont('helvetica', 'bold');
              headers[0].forEach((header, index) => {
                const text = String(header).substring(0, index === 2 ? 20 : 15);
                doc.text(text, headerX[index], yPos);
              });
              doc.setFont('helvetica', 'normal');
              yPos += 7;
              doc.line(14, yPos, 280, yPos);
              yPos += 4;
            }

            // Dessiner une ligne alternative pour faciliter la lecture
            if (rowCount % 2 === 0) {
              doc.setFillColor(245, 245, 245);
              doc.rect(14, yPos - 3, 266, 4, 'F');
            }

            row.forEach((cell, index) => {
              const cellValue = cell || 'N/A';
              let text = String(cellValue);

              // Limiter la longueur selon la colonne
              const maxLength = index === 2 ? 30 : index === 1 ? 25 : 20;
              if (text.length > maxLength) {
                text = text.substring(0, maxLength - 3) + '...';
              }

              doc.setTextColor(0, 0, 0);
              doc.text(text, headerX[index], yPos);
            });

            yPos += 5;
            rowCount++;
          });
        }

        const filename = `controleurs_${this.campagneDisplayYear || new Date().getFullYear()}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);

        this.$message.success('Export PDF réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export PDF:', error);
        const errorMessage = error.message || 'Erreur inconnue';
        this.$message.error(`Erreur lors de l'export PDF: ${errorMessage}`);
      }
    },
    handleExportProducteurs(format) {
      if (!format) {
        return;
      }

      const dataToExport = this.filteredProducteursList;

      if (dataToExport.length === 0) {
        this.$message.warning('Aucune donnée à exporter');
        this.producteurExportFormat = null;
        return;
      }

      if (format === 'excel') {
        this.exportProducteursToExcel(dataToExport);
      } else if (format === 'pdf') {
        this.exportProducteursToPDF(dataToExport);
      }

      // Réinitialiser le select après l'export
      this.producteurExportFormat = null;
    },
    exportProducteursToExcel(data) {
      try {
        const header = ['Nom', 'Prénom', 'Numéro', 'Code'];

        const exportData = data.map(producteur => [
          producteur.nom || 'N/A',
          producteur.prenom || 'N/A',
          this.formatProducteurNumero(producteur),
          this.formatProducteurCode(producteur),
        ]);

        const filename = `producteurs_${this.campagneDisplayYear || 'export'}_${new Date().toISOString().split('T')[0]}`;

        export_json_to_excel({
          header,
          data: exportData,
          filename,
          autoWidth: true,
          bookType: 'xlsx',
        });

        this.$message.success('Export Excel réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export Excel:', error);
        this.$message.error('Erreur lors de l\'export Excel');
      }
    },
    exportProducteursToPDF(data) {
      try {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });

        // Titre
        doc.setFontSize(16);
        doc.text('Liste des Producteurs', 14, 15);

        // Informations de la campagne
        doc.setFontSize(10);
        doc.text(`Campagne: ${this.campagneDisplayYear || 'N/A'}`, 14, 22);
        doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, 14, 27);
        doc.text(`Nombre de producteurs: ${data.length}`, 14, 32);

        // Préparer les données pour le tableau
        const tableData = data.map(producteur => [
          producteur.nom || 'N/A',
          producteur.prenom || 'N/A',
          this.formatProducteurNumero(producteur),
          this.formatProducteurCode(producteur),
        ]);

        const headers = [['Nom', 'Prénom', 'Numéro', 'Code']];

        // Utiliser autoTable si disponible, sinon créer un tableau simple
        if (doc.autoTable) {
          doc.autoTable({
            head: headers,
            body: tableData,
            startY: 38,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { cellWidth: 60 },
              2: { cellWidth: 50 },
              3: { cellWidth: 50 },
            },
            margin: { left: 14, right: 14 },
          });
        } else {
          // Fallback: tableau simple sans autoTable
          let yPos = 40;
          doc.setFontSize(7);

          // En-têtes avec fond gris
          doc.setFillColor(200, 200, 200);
          doc.rect(14, yPos - 5, 182, 6, 'F');
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');
          doc.text('Nom', 16, yPos);
          doc.text('Prénom', 66, yPos);
          doc.text('Numéro', 116, yPos);
          doc.text('Code', 166, yPos);

          yPos += 8;

          // Données
          doc.setFont('helvetica', 'normal');
          tableData.forEach((row, index) => {
            if (yPos > 280) {
              try {
                doc.addPage({
                  orientation: 'portrait',
                  format: 'a4',
                });
              } catch (e) {
                doc.addPage();
              }
              yPos = 15;
            }

            row.forEach((cell, cellIndex) => {
              const cellValue = cell || 'N/A';
              let text = String(cellValue);
              const maxLength = 20;
              if (text.length > maxLength) {
                text = text.substring(0, maxLength - 3) + '...';
              }

              doc.setTextColor(0, 0, 0);
              const xPositions = [16, 66, 116, 166];
              doc.text(text, xPositions[cellIndex], yPos);
            });

            yPos += 6;
          });
        }

        const fileName = `producteurs_${this.campagneDisplayYear || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        this.$message.success('Export PDF réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export PDF:', error);
        const errorMessage = error.message || 'Erreur inconnue';
        this.$message.error(`Erreur lors de l'export PDF: ${errorMessage}`);
      }
    },
    handleExportParcelles(format) {
      if (!format) {
        return;
      }

      const dataToExport = this.filteredParcellesList;

      if (dataToExport.length === 0) {
        this.$message.warning('Aucune donnée à exporter');
        this.parcelleExportFormat = null;
        return;
      }

      if (format === 'excel') {
        this.exportToExcel(dataToExport);
      } else if (format === 'pdf') {
        this.exportToPDF(dataToExport);
      }

      // Réinitialiser le select après l'export
      this.parcelleExportFormat = null;
    },
    exportToExcel(data) {
      try {
        const header = [
          'Code',
          'Producteur',
          'Groupement',
          'Localisation',
          'Superficie (ha)',
          'Statut',
          'Certification',
          'Culture',
        ];

        const exportData = data.map(parcelle => {
          const code = parcelle.code || (parcelle.parcelle && parcelle.parcelle.code) || 'N/A';
          const producteur = this.formatProducteurName(parcelle.producteur || parcelle.producteur_data);
          const groupement = parcelle.groupement || parcelle.groupement_nom || 'N/A';
          const localisation = this.formatLocalisation(parcelle);
          const superficie = this.formatNumber(parcelle.superficie || (parcelle.parcelle && parcelle.parcelle.superficie) || 0);
          const statut = this.getParcelleStatut(parcelle) || 'N/A';
          const certification = statut === 'AB' ? 'Bio' : 'Non-bio';
          const culture = this.formatCultures(parcelle.produits || parcelle.cultures);

          return [code, producteur, groupement, localisation, superficie, statut, certification, culture];
        });

        const filename = `parcelles_${this.campagneDisplayYear || new Date().getFullYear()}_${new Date().toISOString().split('T')[0]}`;

        export_json_to_excel({
          header,
          data: exportData,
          filename,
          autoWidth: true,
          bookType: 'xlsx',
        });

        this.$message.success('Export Excel réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export Excel:', error);
        this.$message.error('Erreur lors de l\'export Excel');
      }
    },
    exportToPDF(data) {
      try {
        // Vérifier que jsPDF est disponible
        if (typeof jsPDF === 'undefined') {
          throw new Error('jsPDF n\'est pas disponible');
        }

        // Créer le document PDF (compatible avec jsPDF 2.x et versions antérieures)
        let doc;
        try {
          // Essayer la syntaxe moderne (jsPDF 2.x+)
          // eslint-disable-next-line new-cap
          doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
          });
        } catch (e) {
          // Fallback vers l'ancienne syntaxe (jsPDF 1.x)
          // eslint-disable-next-line new-cap
          doc = new jsPDF('landscape', 'mm', 'a4');
        }

        // Titre
        doc.setFontSize(16);
        doc.text('Liste des Parcelles', 14, 15);

        // Informations de la campagne
        doc.setFontSize(10);
        doc.text(`Campagne: ${this.campagneDisplayYear || 'N/A'}`, 14, 22);
        doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, 14, 27);
        doc.text(`Nombre de parcelles: ${data.length}`, 14, 32);

        // Préparer les données pour le tableau
        const tableData = data.map(parcelle => {
          const code = parcelle.code || (parcelle.parcelle && parcelle.parcelle.code) || 'N/A';
          const producteur = this.formatProducteurName(parcelle.producteur || parcelle.producteur_data);
          const groupement = parcelle.groupement || parcelle.groupement_nom || 'N/A';
          const localisation = this.formatLocalisation(parcelle);
          const superficie = this.formatNumber(parcelle.superficie || (parcelle.parcelle && parcelle.parcelle.superficie) || 0);
          const statut = this.getParcelleStatut(parcelle) || 'N/A';
          const certification = statut === 'AB' ? 'Bio' : 'Non-bio';
          const culture = this.formatCultures(parcelle.produits || parcelle.cultures);

          return [code, producteur, groupement, localisation, superficie, statut, certification, culture];
        });

        const headers = [
          ['Code', 'Producteur', 'Groupement', 'Localisation', 'Superficie (ha)', 'Statut', 'Certification', 'Culture'],
        ];

        // Utiliser autoTable si disponible, sinon créer un tableau simple
        if (doc.autoTable) {
          doc.autoTable({
            head: headers,
            body: tableData,
            startY: 38,
            styles: { fontSize: 7 },
            headStyles: { fillColor: [66, 139, 202] },
            columnStyles: {
              0: { cellWidth: 25 },
              1: { cellWidth: 40 },
              2: { cellWidth: 35 },
              3: { cellWidth: 45 },
              4: { cellWidth: 25 },
              5: { cellWidth: 20 },
              6: { cellWidth: 25 },
              7: { cellWidth: 35 },
            },
            margin: { left: 14, right: 14 },
          });
        } else {
          // Fallback: tableau simple sans autoTable
          let yPos = 40;
          doc.setFontSize(7);

          // En-têtes avec fond gris
          doc.setFillColor(200, 200, 200);
          doc.rect(14, yPos - 5, 266, 6, 'F');
          doc.setTextColor(0, 0, 0);
          doc.setFont('helvetica', 'bold');

          const headerX = [18, 45, 85, 125, 175, 205, 235, 265];
          headers[0].forEach((header, index) => {
            const text = String(header).substring(0, index === 3 ? 15 : 12);
            doc.text(text, headerX[index], yPos);
          });

          doc.setFont('helvetica', 'normal');
          yPos += 7;

          // Ligne de séparation
          doc.setDrawColor(200, 200, 200);
          doc.line(14, yPos, 280, yPos);
          yPos += 4;

          // Données avec gestion de plusieurs pages
          let rowCount = 0;
          tableData.forEach((row, rowIndex) => {
            // Vérifier si on doit créer une nouvelle page
            if (yPos > 185) {
              try {
                doc.addPage({
                  orientation: 'landscape',
                  format: 'a4',
                });
              } catch (e) {
                // Fallback pour l'ancienne syntaxe
                doc.addPage('landscape');
              }
              yPos = 15;

              // Réimprimer les en-têtes
              doc.setFillColor(200, 200, 200);
              doc.rect(14, yPos - 5, 266, 6, 'F');
              doc.setFont('helvetica', 'bold');
              headers[0].forEach((header, index) => {
                const text = String(header).substring(0, index === 3 ? 15 : 12);
                doc.text(text, headerX[index], yPos);
              });
              doc.setFont('helvetica', 'normal');
              yPos += 7;
              doc.line(14, yPos, 280, yPos);
              yPos += 4;
            }

            // Dessiner une ligne alternative pour faciliter la lecture
            if (rowCount % 2 === 0) {
              doc.setFillColor(245, 245, 245);
              doc.rect(14, yPos - 3, 266, 4, 'F');
            }

            row.forEach((cell, index) => {
              const cellValue = cell || 'N/A';
              let text = String(cellValue);

              // Limiter la longueur selon la colonne
              const maxLength = index === 3 ? 25 : index === 1 ? 18 : 12;
              if (text.length > maxLength) {
                text = text.substring(0, maxLength - 3) + '...';
              }

              doc.setTextColor(0, 0, 0);
              doc.text(text, headerX[index], yPos);
            });

            yPos += 5;
            rowCount++;
          });
        }

        const filename = `parcelles_${this.campagneDisplayYear || new Date().getFullYear()}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);

        this.$message.success('Export PDF réussi');
      } catch (error) {
        console.error('Erreur lors de l\'export PDF:', error);
        const errorMessage = error.message || 'Erreur inconnue';
        this.$message.error(`Erreur lors de l'export PDF: ${errorMessage}`);
      }
    },
    isParcelleGeolocated(parcelle) {
      // Gérer les différentes structures de données
      const parcelleData = parcelle.parcelle || parcelle;

      // Récupérer le champ GPS
      const gpsData = parcelleData.gps || parcelle.gps;

      if (!gpsData) {
        return false;
      }

      // Vérifier si les données GPS sont valides (non vides)
      try {
        if (typeof gpsData === 'string') {
          // Essayer de parser comme JSON
          try {
            const parsed = JSON.parse(gpsData);
            if (Array.isArray(parsed)) {
              return parsed.length > 0;
            }
            return parsed !== null && parsed !== undefined;
          } catch (parseError) {
            // Si ce n'est pas du JSON, vérifier si la chaîne n'est pas vide
            return gpsData.trim() !== '';
          }
        } else if (Array.isArray(gpsData)) {
          return gpsData.length > 0;
        }
        return gpsData !== null && gpsData !== undefined;
      } catch (e) {
        // En cas d'erreur, considérer comme non géolocalisé
        return false;
      }
    },
    formatLocalisation(parcelle) {
      // Gérer les différentes structures de données
      const parcelleData = parcelle.parcelle || parcelle;

      // Récupérer le champ GPS
      const gpsData = parcelleData.gps || parcelle.gps;

      if (!gpsData) {
        return 'N/A';
      }

      // Essayer de parser le GPS si c'est une chaîne JSON
      let gpsPoints = [];
      try {
        if (typeof gpsData === 'string') {
          // Essayer de parser comme JSON
          const parsed = JSON.parse(gpsData);
          gpsPoints = Array.isArray(parsed) ? parsed : [parsed];
        } else if (Array.isArray(gpsData)) {
          gpsPoints = gpsData;
        } else {
          gpsPoints = [gpsData];
        }
      } catch (e) {
        // Si le parsing échoue, traiter comme une chaîne simple
        // Peut-être un format personnalisé, on l'affiche tel quel
        return gpsData;
      }

      // Formater les points GPS
      const formattedPoints = gpsPoints.map((point) => {
        // Si c'est un tableau [lng, lat] ou [lat, lng]
        if (Array.isArray(point) && point.length >= 2) {
          // Détecter l'ordre: au Togo, latitude ~6-11°, longitude ~0-2°
          const val1 = parseFloat(point[0]);
          const val2 = parseFloat(point[1]);
          if (val1 > 5 && val1 < 12) {
            // C'est latitude en premier
            return `${val1}, ${val2}`;
          }
          // C'est longitude en premier
          return `${val2}, ${val1}`;
        }
        // Si c'est un objet avec longitude et latitude
        if (typeof point === 'object' && point !== null) {
          if (point.longitude !== undefined && point.latitude !== undefined) {
            return `${point.latitude}, ${point.longitude}`;
          }
        }
        // Si c'est une chaîne, l'afficher tel quel
        return String(point);
      }).filter(Boolean);

      return formattedPoints.length > 0 ? formattedPoints.join(' | ') : 'N/A';
    },
    getParcelleStatut(parcelle) {
      // Gérer les différentes structures de données
      return parcelle.statut || (parcelle.parcelle && parcelle.parcelle.statut) || null;
    },
    formatCultures(cultures) {
      if (!cultures) {
        return 'N/A';
      }
      if (Array.isArray(cultures)) {
        if (cultures.length === 0) {
          return 'N/A';
        }
        // Si ce sont des objets avec une propriété 'nom'
        if (cultures[0] && typeof cultures[0] === 'object' && cultures[0].nom) {
          return cultures.map(c => c.nom).join(', ');
        }
        // Si ce sont des strings
        return cultures.join(', ');
      }
      if (typeof cultures === 'string') {
        return cultures;
      }
      return 'N/A';
    },
    getStatutTagType(statut) {
      if (statut === 'AB') {
        return 'success';
      }
      if (statut === 'Conversion') {
        return 'warning';
      }
      return 'info';
    },
    voirDetailsParcelle(parcelle) {
      // Naviguer vers la page de détails de la parcelle
      const parcelleId = parcelle.id || parcelle.parcelle_id || (parcelle.parcelle && parcelle.parcelle.id);
      const parcelleCode = parcelle.code || (parcelle.parcelle && parcelle.parcelle.code);

      if (parcelleId) {
        this.$router.push({
          name: 'parcelles-unique',
          params: { id: parcelleId },
        });
        this.closeKpiModal();
      } else if (parcelleCode) {
        // Si on a le code mais pas l'ID, naviguer par code
        this.$router.push({
          name: 'parcelles',
          query: { code: parcelleCode },
        });
        this.closeKpiModal();
      } else {
        this.$message.warning('ID ou code de parcelle introuvable');
      }
    },
    async voirCarteParcelle(parcelle) {
      // Ouvrir la modal avec la carte de la parcelle
      const parcelleId = parcelle.id || parcelle.parcelle_id || (parcelle.parcelle && parcelle.parcelle.id);

      if (!parcelleId) {
        this.$message.warning('ID de parcelle introuvable');
        return;
      }

      this.selectedParcelleForCarte = parcelle;
      this.carteModalKey += 1;
      this.coordonneesParcelle = [];
      this.loadingCoordonnees = true;

      // Charger les coordonnées de la parcelle
      await this.loadCoordonneesParcelle(parcelleId);

      // Ouvrir la modal
      this.$nextTick(() => {
        this.carteModalVisible = true;
      });
    },
    async loadCoordonneesParcelle(parcelleId) {
      try {
        // D'abord récupérer la parcelle
        const parcelleResponse = await parcelleResource.get(parcelleId);
        if (parcelleResponse && parcelleResponse.data) {
          const parcelle = parcelleResponse.data;

          // Les coordonnées peuvent être dans coordonnees ou directement dans la parcelle
          if (parcelle.coordonnees && Array.isArray(parcelle.coordonnees) && parcelle.coordonnees.length > 0) {
            // Transformer les coordonnées au format [longitude, latitude]
            this.coordonneesParcelle = this.coordonneesList(parcelle.coordonnees);
          } else {
            // Si pas de coordonnées dans la parcelle, les récupérer depuis l'API coordonnees
            try {
              const coordonneesResponse = await coordonneeResource.list({
                parcelle_id: parcelleId,
                campagne_agricole_id: this.campagneId || this.camp_id,
                limit: 1000,
              });

              if (coordonneesResponse && coordonneesResponse.data && Array.isArray(coordonneesResponse.data)) {
                this.coordonneesParcelle = coordonneesResponse.data.map(coord => {
                  if (coord.longitude && coord.latitude) {
                    return [parseFloat(coord.longitude), parseFloat(coord.latitude)];
                  }
                  return null;
                }).filter(coord => coord !== null);
              }
            } catch (coordError) {
              console.error('Erreur lors du chargement des coordonnées depuis l\'API:', coordError);
            }
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des coordonnées:', error);
        this.$message.error('Erreur lors du chargement des coordonnées de la parcelle');
      } finally {
        this.loadingCoordonnees = false;
      }
    },
    initMap() {
      // Détruire la carte existante si elle existe
      if (this.mapInstance) {
        this.mapInstance.remove();
        this.mapInstance = null;
      }

      // Attendre que le conteneur soit disponible
      this.$nextTick(() => {
        const container = document.getElementById('carteParcelleContainer');
        if (!container) {
          console.error('Conteneur de carte introuvable');
          return;
        }

        mapboxgl.accessToken = this.mapboxAccessToken;

        const nav = new mapboxgl.NavigationControl();

        // Calculer le centre des coordonnées
        let center = [1.1868609, 6.2056304]; // Centre par défaut
        if (this.coordonneesParcelle.length > 0) {
          const lngs = this.coordonneesParcelle.map(c => c[0]);
          const lats = this.coordonneesParcelle.map(c => c[1]);
          center = [
            (Math.min(...lngs) + Math.max(...lngs)) / 2,
            (Math.min(...lats) + Math.max(...lats)) / 2,
          ];
        }

        const map = new mapboxgl.Map({
          container: 'carteParcelleContainer',
          style: 'mapbox://styles/mapbox/streets-v12',
          center: center,
          zoom: 15,
        });

        this.mapInstance = map;

        const geojson = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                properties: {},
                coordinates: this.coordonneesParcelle,
              },
            },
          ],
        };

        map.on('load', () => {
          map.addSource('LineString', {
            type: 'geojson',
            data: geojson,
          });

          map.addLayer({
            id: 'LineString',
            type: 'line',
            source: 'LineString',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#ec1232',
              'line-width': 2,
            },
          });

          // Ajouter un polygone rempli
          if (this.coordonneesParcelle.length > 0) {
            // Fermer le polygone si nécessaire
            const closedCoords = [...this.coordonneesParcelle];
            if (closedCoords[0][0] !== closedCoords[closedCoords.length - 1][0] ||
                closedCoords[0][1] !== closedCoords[closedCoords.length - 1][1]) {
              closedCoords.push(closedCoords[0]);
            }

            map.addSource('Polygon', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [closedCoords],
                },
              },
            });

            map.addLayer({
              id: 'Polygon',
              type: 'fill',
              source: 'Polygon',
              paint: {
                'fill-color': '#ec1232',
                'fill-opacity': 0.3,
              },
            });
          }

          // Bouton zoom
          const zoomButton = document.getElementById('zoomto-parcelle');
          if (zoomButton) {
            zoomButton.addEventListener('click', () => {
              const coordinates = geojson.features[0].geometry.coordinates;
              const bounds = new mapboxgl.LngLatBounds(
                coordinates[0],
                coordinates[0]
              );

              for (const coord of coordinates) {
                bounds.extend(coord);
              }

              map.fitBounds(bounds, {
                padding: 20,
              });
            });
          }
        });

        map.addControl(nav, 'bottom-right');

        const geolocate = new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
        });
        map.addControl(geolocate, 'bottom-right');
      });
    },
    closeCarteModal() {
      // Détruire la carte avant de fermer la modal
      if (this.mapInstance) {
        this.mapInstance.remove();
        this.mapInstance = null;
      }
      this.carteModalVisible = false;
      this.selectedParcelleForCarte = null;
      this.coordonneesParcelle = [];
      this.$nextTick(() => {
        this.carteModalKey += 1;
      });
    },
    coordonneesList(parcelleCoords) {
      // Transformer les coordonnées au format [longitude, latitude] pour Mapbox
      if (!parcelleCoords || !Array.isArray(parcelleCoords)) {
        return [];
      }
      const cords = [];
      parcelleCoords.forEach(element => {
        if (element.longitude && element.latitude) {
          // Mapbox utilise [longitude, latitude]
          cords.push([parseFloat(element.longitude), parseFloat(element.latitude)]);
        } else if (Array.isArray(element) && element.length >= 2) {
          // Si c'est déjà un tableau
          const val1 = parseFloat(element[0]);
          const val2 = parseFloat(element[1]);
          // Détecter si c'est [lat, lng] ou [lng, lat]
          // Au Togo: latitude ~6-11°, longitude ~0-2°
          if (val1 > 5 && val1 < 12) {
            // C'est latitude en premier, inverser
            cords.push([val2, val1]);
          } else {
            // C'est déjà [lng, lat]
            cords.push([val1, val2]);
          }
        }
      });

      // Réorganiser pour fermer le polygone (comme dans UniquePage.vue)
      if (cords.length > 0) {
        const firstValue = cords[0];
        // S'assurer que le polygone est fermé
        if (cords[cords.length - 1][0] !== firstValue[0] || cords[cords.length - 1][1] !== firstValue[1]) {
          cords.push(firstValue);
        }
      }

      return cords;
    },
    getKpiIcon(title) {
      const iconMap = {
        'Parcelles': '📍',
        'Contrôleurs': '👨‍💼',
        'Producteurs': '👥',
        'Groupements actifs': '🏘️',
        'Audits': '🔍',
        'Contrôles Internes': '✅',
        'Formations': '📚',
        'Surface Déclassée': '⚠️',
        'Surface Certifiée': '🌱',
        'Quantité Collectée': '📦',
      };
      return iconMap[title] || '📊';
    },
  },
};
</script>

<style lang="scss" scoped>
.kpi-filter-row {
  padding: 12px 0;
  border-bottom: 1px solid #e4e7ed;
  margin-bottom: 18px;
}

.kpi-filter-row .periode-filter {
  justify-content: center;
  margin: 0 auto;
}

.kpi-filter-row .periode-filter ::v-deep .el-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.kpi-filter-row .periode-filter .custom-dates {
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
}

.kpi-filter-row.quick-filters {
  border-bottom: none;
  padding: 0;
  gap: 20px;
}

.kpi-filter-row.quick-filters .extra-filter {
  min-width: 120px;
}

.quick-filter-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quick-filter-select .select-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
}

.kpis-placeholder {
  padding: 40px 20px;
  text-align: center;
  color: #909399;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.kpis-placeholder p {
  margin: 8px 0;
  font-size: 16px;
}

.kpis-placeholder .filters-info {
  margin-top: 20px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  font-size: 14px;
  color: #606266;
}

.kpis-placeholder .filters-info span {
  color: #00ab53;
  font-weight: 500;
}

.kpis-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px;
}

.kpis-grid .kpi-item {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 13px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex: 1 1 220px;
  cursor: pointer;
}

.kpis-grid .kpi-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 171, 83, 0.15);
  border-color: #00ab53;
}

.kpis-grid .kpi-item.kpi-item-large {
  flex: 0 0 auto;
}

.kpis-grid .kpi-icon {
  font-size: 25px;
  line-height: 1;
}

.kpis-grid .kpi-content {
  flex: 1;
}

.kpis-grid .kpi-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
  font-weight: 500;
}

.kpis-grid .kpi-value {
  font-size: 25px;
  font-weight: 700;
  color: #00ab53;
  line-height: 1.2;
}

@media (max-width: 768px) {
  .kpi-filter-row.quick-filters {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .quick-filter-select:nth-child(odd) {
    margin-right: 0;
  }
}

@media (max-width: 480px) {
  .kpi-filter-row.quick-filters {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 768px) {
  .kpi-filter-row .periode-filter {
    width: 100%;
    margin-left: 0 !important;
    margin-top: 16px;
    flex-direction: column;
    align-items: flex-start;
  }

  .kpis-grid {
    padding: 15px;
    gap: 15px;
  }

  .kpis-grid .kpi-item {
    flex: 1 1 200px;
  }

  .kpis-grid .kpi-icon {
    font-size: 28px;
  }

.kpis-grid .kpi-value {
  font-size: 24px;
}
}

.kpi-modal-content {
  padding: 0;
}

.kpi-modal-header {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 8px;
  margin-bottom: 20px;
}

.kpi-modal-icon {
  font-size: 48px;
  line-height: 1;
  flex-shrink: 0;
}

.kpi-modal-info {
  flex: 1;
}

.kpi-modal-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.kpi-modal-value {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.value-label {
  font-size: 14px;
  color: #909399;
  font-weight: 500;
}

.value-number {
  font-size: 32px;
  font-weight: 700;
  color: #00ab53;
  line-height: 1;
}

.kpi-modal-body {
  padding: 0 20px 20px 20px;
}

.kpi-modal-description {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.kpi-modal-description strong {
  color: #00ab53;
  font-weight: 600;
}

::v-deep .kpi-detail-modal .el-dialog__header {
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #e4e7ed;
}

::v-deep .kpi-detail-modal .el-dialog__title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

::v-deep .kpi-detail-modal .el-dialog__wrapper {
  overflow: hidden !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
  width: 100vw !important;
  z-index: 2000 !important;
}

::v-deep .kpi-detail-modal .v-modal {
  overflow: hidden !important;
}

::v-deep .kpi-detail-modal {
  will-change: auto;
  overflow: hidden !important;
}

::v-deep .kpi-detail-modal .el-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

::v-deep .kpi-detail-modal .el-dialog__header {
  will-change: auto;
  flex-shrink: 0;
}

::v-deep .kpi-detail-modal .el-dialog__body {
  padding: 20px;
  will-change: auto;
  overflow: hidden !important;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

html.kpi-modal-open,
html.el-popup-parent--hidden,
html.v-modal-parent--hidden,
body.kpi-modal-open,
body.el-popup-parent--hidden,
body.v-modal-parent--hidden {
  overflow: hidden !important;
  padding-right: 0 !important;
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
}

/* Bloquer le scroll sur tous les éléments parents potentiels */
#app.kpi-modal-open,
.app-container.kpi-modal-open,
.main-container.kpi-modal-open {
  overflow: hidden !important;
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
}

/* S'assurer que le loading overlay reste dans le conteneur et ne remonte pas au header */
/* Forcer le loading overlay à rester dans le conteneur uniquement (ne pas utiliser fixed qui remonte au header) */
::v-deep .kpis-placeholder .el-loading-mask {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 2000 !important;
  margin: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
}

/* S'assurer que le loading ne s'étend pas en dehors du conteneur */
::v-deep .dashboard-loading-overlay {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 2000 !important;
}

.parcelles-table-container {
  margin-top: 20px;
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #909399;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.carte-modal-content {
  padding: 0;
}

.carte-info {
  padding: 15px;
  background: #f5f7fa;
  border-radius: 6px;
  margin-bottom: 15px;
}

.carte-info p {
  margin: 5px 0;
  font-size: 14px;
}

.carte-info strong {
  color: #303133;
  font-weight: 600;
}

#carte-parcelle-container {
  position: relative;
  width: 100%;
  margin-top: 15px;
}

#carteParcelleContainer {
  width: 100%;
  height: 500px;
  border-radius: 6px;
  overflow: hidden;
}

.btn-control-carte {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  padding: 8px 16px;
  background: #00ab53;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.btn-control-carte:hover {
  background: #009944;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.carte-info-floating {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  padding: 12px 16px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 300px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.carte-info-floating p {
  margin: 6px 0;
  font-size: 13px;
  line-height: 1.4;
  color: #303133;
}

.carte-info-floating strong {
  color: #606266;
  font-weight: 600;
}

.loading-coordonnees,
.no-coordonnees {
  padding: 40px;
  text-align: center;
  color: #909399;
}

::v-deep .carte-parcelle-modal.fullscreen-modal {
  margin: 0 !important;
  height: 100vh;
}

::v-deep .carte-parcelle-modal.fullscreen-modal .el-dialog {
  margin: 0 !important;
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
}

::v-deep .carte-parcelle-modal.fullscreen-modal .el-dialog__header {
  padding: 20px 20px 10px 20px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

::v-deep .carte-parcelle-modal.fullscreen-modal .el-dialog__title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

::v-deep .carte-parcelle-modal.fullscreen-modal .el-dialog__body {
  padding: 10px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

::v-deep .carte-parcelle-modal.fullscreen-modal .el-dialog__footer {
  flex-shrink: 0;
  padding: 15px 20px;
  border-top: 1px solid #e4e7ed;
}

.carte-fullscreen-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.carte-fullscreen-map {
  width: 100%;
  flex: 1;
  min-height: 500px;
  height: calc(100vh - 300px);
}
</style>

